import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Select,
  Tooltip,
  Upload,
} from 'antd'
import moment from 'moment'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import FadeIn from 'src/components/styled/FadeIn'
import { API } from 'src/utils/interceptor'
import { canManageAnyVenue } from 'src/utils/adminAccess'
import { getVenueRoleDisplayName } from 'src/utils/venueRoleCapabilities'
import VenueRoleSelect from 'src/components/VenueRoleSelect'
import { VENUE_ADMIN } from 'src/utils/roles'
import { venueGrossProfitPath } from 'src/screens/orders/grossProfitRange'
import { listRevenueCategories } from 'src/utils/revenueCategoryApi'
import { describeRevenueCategoryMultiplier } from 'src/utils/revenueLinePreview'
import { filterVenuesForGroupFill } from 'src/screens/venues/venueListFilters'
import { SHOPPING_LIST_REPLACES } from 'src/screens/venue-groups/bulkActionConfirmation'
import {
  GroupHero,
  GroupLead,
  GroupPage,
  GroupSurface,
} from 'src/screens/venue-groups/venueGroupChrome'
import { fetchEveryVenuePage } from 'src/screens/venues/venueListPages'
import {
  ambiguousPasteLines,
  matchPastedVenueNames,
  pasteSelectionFeedback,
  selectAllowedVenueMatches,
  venuesForPasteJump,
} from 'src/screens/venue-groups/VenueGroupCreate'
import { formatShareConfirmation } from 'src/screens/venue-groups/bulkActionConfirmation'
import { formatGroupLastRun } from 'src/screens/venue-groups/VenueGroups'
import {
  GROUP_MISSES_TAB_QUERY,
  receiptProblemLines,
  restrictedSupplierReceiptLines,
  shareResultsHaveMisses,
  shoppingListReceiptLines,
} from 'src/screens/venue-groups/runReceipt'
import VenueGroupRunDialog from 'src/screens/venue-groups/VenueGroupRunDialog'
import SourcePicker, {
  PRODUCT_LOOKUP_NOTE,
} from 'src/screens/venue-groups/SourcePicker'
import {
  RejectedNotification,
  SuccessNotification,
  WarningNotification,
} from 'src/notifications/SuccessNotification'

export const REVENUE_SHARE_DOES_NOT_COPY_SALES =
  'This replaces the categories on the other venues. It does not copy the sales amounts.'

/** Group page: the list is already filtered to venues this viewer can manage. */
export const groupVenuesLead = count => {
  const total = Number(count) || 0
  const venues =
    total === 1 ? '1 venue you can manage' : `${total} venues you can manage`
  return `${venues} in this group. Copy from one of them, or add more.`
}

export const GROUP_SHARE_LEAD =
  'Choose something from one site. It is pasted onto the venues you tick below (default: all venues you can manage here). Nothing is sent until you review and confirm.'

/** One-share subset vs a lasting layout — avoids a second group for “bars only”. */
export const GROUP_SHARE_SUBSET_TIP =
  'Untick sites for a one-off (for example bars only). For a lasting bars or kitchens layout, Duplicate / Save as and trim that copy — you do not need a second full membership graph by hand.'

export const GROUP_SHARE_USES_OWN_VENUE_PICKER = ['invites', 'remove', 'person']

/** Quiet caption above the member list — membership comes only from the API payload. */
export const GROUP_MEMBERS_CAPTION = 'Venues you can manage'

/**
 * Keep a share subset when membership changes; fall back to every member.
 */
export const syncShareTargetIds = (currentIds, memberVenues) => {
  const all = (memberVenues || [])
    .map(venue => String(venue._id))
    .filter(Boolean)
  const kept = (currentIds || []).map(String).filter(id => all.includes(id))
  return kept.length > 0 ? kept : all
}

/**
 * Attach venueIds only when the user narrowed the share (Invite/Remove already
 * send their own ticks).
 */
export const sharePayloadWithTargets = (payload, targetIds, memberVenues) => {
  const all = (memberVenues || [])
    .map(venue => String(venue._id))
    .filter(Boolean)
  const chosen = [
    ...new Set((targetIds || []).map(String).filter(id => all.includes(id))),
  ]
  if (chosen.length === 0 || chosen.length === all.length) {
    return { ...(payload || {}) }
  }
  return { ...(payload || {}), venueIds: chosen }
}

/** Sticky subnav on the group page — one section at a time. */
export const GROUP_PAGE_TABS = {
  venues: 'venues',
  share: 'share',
  misses: 'misses',
}

export const groupPageTabItems = (missCount = 0) => [
  { id: GROUP_PAGE_TABS.venues, label: 'Venues' },
  { id: GROUP_PAGE_TABS.share, label: 'Share' },
  {
    id: GROUP_PAGE_TABS.misses,
    label: missCount > 0 ? `Misses (${missCount})` : 'Misses',
  },
]

/** Rename / archive: group owner (account or createdBy) or an OMS admin. */
export const canEditVenueGroupMeta = (account, group) => {
  if (!account || !group) return false
  if (account.isAdmin === true) return true
  const me = String(account._id || '')
  if (!me) return false
  return (
    me === String(group.account || '') || me === String(group.createdBy || '')
  )
}

/** Default name when duplicating: “Hotel bars copy” (fits the 80-char name cap). */
export const suggestedDuplicateGroupName = name => {
  const suffix = ' copy'
  const base = String(name || '').trim() || 'Group'
  return `${base.slice(0, Math.max(0, 80 - suffix.length))}${suffix}`
}

/**
 * Member rows for the group page. Membership is only `group.venues` from the
 * payload; the catalogue may enrich activity fields, never add secret venues.
 */
export const enrichGroupMemberVenues = (groupVenues, catalogueById) =>
  (groupVenues || []).map(venue => {
    const extra = catalogueById?.get?.(String(venue._id)) || {}
    return {
      ...extra,
      ...venue,
      _id: venue._id || extra._id,
      title: venue.title || extra.title,
      activityColor: venue.activityColor || extra.activityColor,
      lastOrderDate: venue.lastOrderDate || extra.lastOrderDate,
    }
  })

/** Drop receipt lines for venues not in the filtered payload (no title leak). */
export const lastRunLinesForMembers = (lines, groupVenues) => {
  const titles = new Set(
    (groupVenues || [])
      .map(venue => String(venue?.title || '').trim())
      .filter(Boolean),
  )
  if (!titles.size) return []
  return (lines || []).filter(line =>
    titles.has(String(line?.title || '').trim()),
  )
}

export const MISMATCH_PAGE_SIZE = 5
/** Collapse the miss list by default once it grows past this many rows. */
export const MISMATCH_COLLAPSE_AT = 3

export const mismatchesStartExpanded = count =>
  (Number(count) || 0) <= MISMATCH_COLLAPSE_AT

export const mismatchShareTypeOptions = rows => {
  const seen = new Map()
  for (const row of rows || []) {
    const action = String(row?.action || '').trim()
    if (!action || seen.has(action)) continue
    seen.set(action, String(row?.label || action).trim() || action)
  }
  return [...seen.entries()]
    .map(([action, label]) => ({ action, label }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

export const filterMismatchesByShareType = (rows, action) => {
  const list = rows || []
  const picked = String(action || 'all')
  if (!picked || picked === 'all') return list
  return list.filter(row => String(row?.action || '') === picked)
}

const namedList = names => {
  const list = [
    ...new Set(
      (names || []).map(name => String(name || '').trim()).filter(Boolean),
    ),
  ]
  if (list.length === 0) return ''
  if (list.length === 1) return list[0]
  if (list.length === 2) return `${list[0]} and ${list[1]}`
  return `${list.slice(0, -1).join(', ')}, and ${list.at(-1)}`
}

export const shortNamedList = (names, max = 3) => {
  const list = [
    ...new Set(
      (names || []).map(name => String(name || '').trim()).filter(Boolean),
    ),
  ]
  if (list.length <= max) return namedList(list)
  const shown = list.slice(0, max - 1)
  const rest = list.length - shown.length
  if (shown.length === 1) return `${shown[0]} and ${rest} more`
  return `${shown.join(', ')}, and ${rest} more`
}

const briefReason = text => {
  const clean = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!clean || clean.length > 120) return ''
  return clean
}

export const mismatchGlance = row => {
  const reason = String(row?.reason || '')
  const suppliers = shortNamedList(row?.supplierNames)
  if (
    row?.action === 'copy_restricted_suppliers' &&
    /supplier is not on this venue/i.test(reason)
  ) {
    if (!suppliers) return 'Supplier is not on this venue'
    const verb = (row?.supplierNames || []).length > 1 ? 'are' : 'is'
    return `${suppliers} ${verb} not on this venue`
  }
  if (/supplier is not on this venue/i.test(reason)) {
    if (!suppliers) return 'Supplier is not switched on'
    const verb = (row?.supplierNames || []).length > 1 ? 'are' : 'is'
    return `${suppliers} ${verb} not switched on`
  }
  if (/local changes|confirmed different days/i.test(reason))
    return 'Kept its own edit'
  if (/not in this venue's catalogue/i.test(reason)) {
    const products = shortNamedList(row?.catalogueProducts)
    return products
      ? `${products} not in the catalogue`
      : 'A product is not in the catalogue'
  }
  if (/missing ingredient/i.test(reason)) {
    const missing = shortNamedList(
      (reason.split(/missing ingredients:/i)[1] || '')
        .split(',')
        .map(part => part.trim().replace(/\.$/, '')),
    )
    return missing ? `Missing ${missing}` : 'An ingredient is missing'
  }
  const short = reason.replace(/\s+/g, ' ').trim()
  return short.length > 80 ? `${short.slice(0, 77)}…` : short
}

export const mismatchAdvice = row => {
  const title = row?.title || 'This venue'
  const reason = String(row?.reason || '')
  const label = String(row?.label || 'share').toLowerCase()
  const suppliers = shortNamedList(row?.supplierNames)
  const products = shortNamedList(row?.catalogueProducts)
  const supplierVerb =
    (row?.supplierNames || []).filter(name => String(name || '').trim())
      .length > 1
      ? 'are'
      : 'is'
  if (/local changes|confirmed different days/i.test(reason)) {
    return {
      issue: `${title} kept its own ${label}. The last share left it as it was.`,
      fix:
        row?.action === 'apply_shopping_list'
          ? `Share the shopping list again and tick Replace all of these anyway next to ${title}. That replaces their list. It cannot be undone.`
          : `Share the ${label} again and tick Replace all of these anyway next to ${title}. That overwrites their edit.`,
    }
  }
  if (
    row?.action === 'copy_restricted_suppliers' &&
    /supplier is not on this venue/i.test(reason)
  ) {
    const who = suppliers || 'The supplier'
    return {
      issue: `${title} did not save the restriction. ${who} ${supplierVerb} not on this venue.`,
      fix: `Switching ${who} on is a separate step. Do that on ${title}, then share the restriction again.`,
    }
  }
  if (/supplier is not on this venue/i.test(reason)) {
    const who = suppliers || 'The supplier'
    return {
      issue: `${title} did not take the shopping list. ${who} ${supplierVerb} not switched on there, so those lines could not be ordered.`,
      fix: `On ${title}, switch on ${who}. Then share the shopping list again. Leave Share only the lines that can land unticked if you want these products on the list.`,
    }
  }
  if (/not in this venue's catalogue/i.test(reason)) {
    const which = products || 'Some products'
    const verb =
      (row?.catalogueProducts || []).filter(name => String(name || '').trim())
        .length === 1
        ? 'is'
        : 'are'
    return {
      issue: `${title} took the rest of the shopping list. ${which} ${verb} not in its catalogue.`,
      fix: `Add ${
        products || 'those products'
      } to ${title} for that supplier, then share again. Or leave them off. The other lines are already on the list.`,
    }
  }
  if (/missing ingredient/i.test(reason)) {
    const missing =
      shortNamedList(
        (reason.split(/missing ingredients:/i)[1] || 'an ingredient')
          .split(',')
          .map(part => part.trim().replace(/\.$/, '')),
      ) || 'an ingredient'
    return {
      issue: `${title} did not take the recipe. Missing ${missing}`,
      fix: `Add ${missing.replace(
        /\.$/,
        '',
      )} on ${title}, then share the recipe again. Tick Force Share Anyway only if you are happy for the dish to be saved without that ingredient.`,
    }
  }
  if (row?.action === 'copy_menu') {
    return {
      issue: [`${title} did not take the menu.`, briefReason(reason)]
        .filter(Boolean)
        .join(' '),
      fix: `Share the menu again. Add any missing dish on ${title} first, or continue and those dishes stay off the copy.`,
    }
  }
  if (row?.action === 'copy_allergen_flags') {
    return {
      issue: [`${title} did not take the allergen flags.`, briefReason(reason)]
        .filter(Boolean)
        .join(' '),
      fix: `Match is by supplier and product code. Switch the supplier on at ${title} if a product was skipped, then share the flags again. Tick replace local changes only for lines someone edited.`,
    }
  }
  if (row?.action === 'assign_revenue_categories') {
    const fix = /renamed/i.test(reason)
      ? 'The category name changed after you confirmed. Share again and tick the name it has now.'
      : /switched off/i.test(reason)
      ? 'Turn that category back on if you still want it shared, then share again.'
      : /same name|with this name/i.test(reason)
      ? `${title} already has a different category with this name. Rename one of them, then share again.`
      : 'Share the categories again. Categories that are off, or renamed since you confirmed, are left out.'
    return {
      issue: [`${title} did not take a revenue category.`, briefReason(reason)]
        .filter(Boolean)
        .join(' '),
      fix,
    }
  }
  if (row?.action === 'invite_people') {
    return {
      issue: [`${title} did not get the invite.`, briefReason(reason)]
        .filter(Boolean)
        .join(' '),
      fix: `Correct the email or the role in the invite list, then send the invites to ${title} again. Someone who is already on the venue is left as they are.`,
    }
  }
  if (row?.action === 'change_venue_person') {
    return {
      issue: [`${title} did not change role.`, briefReason(reason)]
        .filter(Boolean)
        .join(' '),
      fix: `Check that person is already on ${title}, pick the role again, and confirm. A person who is not on the venue has to be invited first.`,
    }
  }
  return {
    issue: [`${title} did not take the ${label}.`, briefReason(reason)]
      .filter(Boolean)
      .join(' '),
    fix: `Fix that on ${title}, then share the ${label} again.`,
  }
}

export const mismatchSlice = (rows, page, size = MISMATCH_PAGE_SIZE) => {
  const list = rows || []
  const pages = Math.max(1, Math.ceil(list.length / size) || 1)
  const current = Math.min(Math.max(Number(page) || 0, 0), pages - 1)
  return {
    rows: list.slice(current * size, current * size + size),
    page: current,
    pages,
  }
}

export const activeRevenueCategories = categories =>
  (categories || []).filter(
    category => category?.active !== false && category?._id,
  )

const REVENUE_VAT_LABELS = {
  standard: 'Standard VAT',
  zeroRated: 'Zero-rated',
  noVat: 'No VAT',
}

export const revenueCategoryTickLabel = category => {
  const entry = describeRevenueCategoryMultiplier(category?.multiplier)
  const entryLabel = entry === 'Amount' ? 'Amount' : `Quantity ${entry}`
  const vat = REVENUE_VAT_LABELS[category?.vatTreatment] || 'No VAT'
  return `${category?.name || ''} · ${entryLabel} · ${vat}`
}

export const INVITE_SHARE_PEOPLE = 'Share people from a venue'
export const INVITE_BY_EMAIL = 'Invite an email to venues in this group'

export const recipeIdsFromText = text => {
  const seen = new Set()
  const ids = []
  String(text || '')
    .split(/[\n,]/)
    .forEach(part => {
      const id = part.trim()
      if (!id || seen.has(id)) return
      seen.add(id)
      ids.push(id)
    })
  return ids
}

const shoppingListCell = value => String(value ?? '').trim()

const SHOPPING_LIST_COLUMNS = {
  product: 'product',
  productid: 'product',
  zone: 'zone',
  defaultquantity: 'defaultQuantity',
  defaultqty: 'defaultQuantity',
  quantity: 'defaultQuantity',
  par: 'parLevel',
  parlevel: 'parLevel',
  sku: 'sku',
  productcode: 'sku',
}

const shoppingListHeaderKey = value =>
  shoppingListCell(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

export const shoppingListItemsFromTable = rows => {
  const cleaned = (rows || [])
    .map(row => (Array.isArray(row) ? row : []).map(shoppingListCell))
    .filter(row => row.some(Boolean))
  if (!cleaned.length) return []
  const mapped = cleaned[0].map(shoppingListHeaderKey)
  const columns = {}
  mapped.forEach((key, index) => {
    const name = SHOPPING_LIST_COLUMNS[key]
    if (name && columns[name] === undefined) columns[name] = index
  })
  const headerRow =
    columns.zone !== undefined &&
    (columns.product !== undefined ||
      columns.sku !== undefined ||
      columns.defaultQuantity !== undefined)
  const body = headerRow ? cleaned.slice(1) : cleaned
  const cell = (row, name, fallback) => {
    const index = headerRow ? columns[name] : fallback
    if (index === undefined || index < 0) return ''
    return row[index] || ''
  }
  return body
    .map(row => {
      const sku = headerRow ? cell(row, 'sku') : ''
      const product = (headerRow ? cell(row, 'product') : row[0]) || sku
      const zone = headerRow ? cell(row, 'zone') : row[1]
      const defaultQuantity = headerRow ? cell(row, 'defaultQuantity') : row[2]
      const parLevel = headerRow ? cell(row, 'parLevel') : row[3]
      const item = { product, zone, defaultQuantity }
      if (parLevel) item.parLevel = parLevel
      if (sku) item.sku = sku
      return item
    })
    .filter(item => item.product && item.zone && item.defaultQuantity)
}

export const shoppingListCsvItems = text => {
  const parsed = Papa.parse(String(text || ''), { skipEmptyLines: 'greedy' })
  return shoppingListItemsFromTable(parsed.data)
}

export const readShoppingListFile = file =>
  new Promise((resolve, reject) => {
    const name = String(file?.name || '')
    const excel = /\.xlsx?$/i.test(name)
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      try {
        if (!excel) {
          resolve(shoppingListCsvItems(String(reader.result || '')))
          return
        }
        const book = XLSX.read(reader.result, { type: 'array' })
        const sheet = book.Sheets[book.SheetNames[0]]
        const rows = sheet
          ? XLSX.utils.sheet_to_json(sheet, {
              header: 1,
              raw: false,
              defval: '',
            })
          : []
        resolve(shoppingListItemsFromTable(rows))
      } catch (error) {
        reject(error)
      }
    }
    if (excel) reader.readAsArrayBuffer(file)
    else reader.readAsText(file)
  })

export const shoppingListRunPayload = (sourceVenueId, csvText) => {
  const csvItems = Array.isArray(csvText)
    ? csvText
    : shoppingListCsvItems(csvText)
  const hasSource = Boolean(sourceVenueId)
  const hasCsv = csvItems.length > 0
  if (hasSource && hasCsv) {
    return { error: 'Choose a source venue or a CSV, not both.' }
  }
  if (hasCsv) return { payload: { csvItems } }
  if (hasSource) return { payload: { sourceVenueId } }
  return { error: '' }
}

const snapshotNames = (names, count) => {
  const shown = (names || [])
    .map(name => String(name || '').trim())
    .filter(Boolean)
    .slice(0, 3)
  const more = Math.max(0, count - shown.length)
  return { shown, more }
}

export const shareSnapshot = ({
  choice,
  csvItems = [],
  listPreview = null,
  recipes = [],
  menu = [],
  supplier = [],
  people = [],
  priceFile = [],
  sheet = [],
  task = [],
  taskRole = '',
  personEmail = '',
  personRole = '',
  actionPreview = null,
}) => {
  if (choice === 'shopping') {
    if (csvItems.length) {
      const names = snapshotNames(
        csvItems.map(item => item.product),
        csvItems.length,
      )
      return {
        summary: `${csvItems.length} ${
          csvItems.length === 1 ? 'line' : 'lines'
        }`,
        ...names,
      }
    }
    if (listPreview) {
      const names = snapshotNames(listPreview.names, listPreview.count)
      return {
        summary: `${listPreview.count} ${
          listPreview.count === 1 ? 'line' : 'lines'
        }`,
        ...names,
      }
    }
    return null
  }
  if (choice === 'recipes' && recipes.length) {
    const names = snapshotNames(
      recipes.map(item => item.title),
      recipes.length,
    )
    return {
      summary: `${recipes.length} ${
        recipes.length === 1 ? 'recipe' : 'recipes'
      }`,
      ...names,
    }
  }
  if (choice === 'menu' && menu.length) {
    const names = snapshotNames(
      menu.map(item => item.title),
      menu.length,
    )
    return {
      summary: `${menu.length} ${menu.length === 1 ? 'menu' : 'menus'}`,
      ...names,
    }
  }
  if (choice === 'supplier' && supplier.length) {
    const names = snapshotNames(
      supplier.map(item => item.title),
      supplier.length,
    )
    return {
      summary: `${supplier.length} ${
        supplier.length === 1 ? 'supplier' : 'suppliers'
      }`,
      ...names,
    }
  }
  if (choice === 'invites' && people.length) {
    const names = snapshotNames(
      people.map(person => person.name || person.email),
      people.length,
    )
    return {
      summary: `${people.length} ${people.length === 1 ? 'person' : 'people'}`,
      ...names,
    }
  }
  if (choice === 'price' && priceFile.length) {
    return {
      summary: '1 price file',
      ...snapshotNames(
        priceFile.map(item => item.title),
        1,
      ),
    }
  }
  if (choice === 'sheet' && sheet.length) {
    return {
      summary: '1 stock-count sheet',
      ...snapshotNames(
        sheet.map(item => item.title),
        1,
      ),
    }
  }
  if (choice === 'task' && task.length) {
    const role = taskRole ? [PERSON_ROLE_LABELS[taskRole] || taskRole] : []
    return {
      summary: '1 home-screen task',
      ...snapshotNames([task[0].title, ...role], 1 + role.length),
    }
  }
  if (choice === 'restricted') return null
  if (choice === 'person' && String(personEmail || '').trim()) {
    const roleLabel = PERSON_ROLE_LABELS[personRole] || personRole
    return {
      summary: String(personEmail).trim(),
      shown: roleLabel ? [roleLabel] : [],
      more: 0,
    }
  }
  const counted = countedPreview(choice, actionPreview)
  if (counted) return counted
  return null
}

const PERSON_ROLE_LABELS = {
  VENUE_ADMIN: 'Admin',
  HEAD_CHEF_GM: 'Head Chef / GM',
  CHEF: 'Chef',
  ORDER_ONLY: 'Order Only',
  KITCHEN_PORTER: 'Kitchen Porter',
}

export const personRunPayload = ({
  email,
  role,
  venueIds,
  venues,
  removing,
}) => {
  const who = String(email || '').trim()
  const titles = (venues || [])
    .filter(venue => (venueIds || []).includes(String(venue._id)))
    .map(venue => venue.title)
  if (removing) {
    return {
      email: who,
      remove: true,
      venueIds,
      removePerson: { email: who, venueTitles: titles },
    }
  }
  return {
    email: who,
    remove: false,
    venueIds,
    role,
    changePerson: {
      email: who,
      roleLabel: PERSON_ROLE_LABELS[role] || role,
      venueTitles: titles,
    },
  }
}

/** Remove Users: Select all ticks venues they are on once roles are known. */
export const personSelectAllVenueIds = ({
  venues,
  personRoles,
  removing,
} = {}) => {
  const all = (venues || []).map(venue => String(venue._id))
  if (!removing) return all
  const roles = personRoles || {}
  if (!Object.keys(roles).length) return all
  return all.filter(id => {
    const row = roles[id]
    return Boolean(row && (row.onVenue || row.owns))
  })
}

/**
 * Fresh people / invite fields after the share type changes.
 * Stops Remove Users picks (email, ticks) leaking into Change a person’s role.
 */
export const peopleShareDefaults = () => ({
  personEmail: '',
  personRole: 'CHEF',
  personVenueIds: [],
  personRoles: {},
  groupPeopleQuery: '',
  groupPeople: [],
  groupPeopleReady: false,
  inviteChoice: '',
  peopleSource: '',
  venuePeople: [],
  peopleTicked: [],
  peopleVenueIds: [],
  inviteEmail: '',
  inviteName: '',
  inviteRole: VENUE_ADMIN,
  inviteVenueIds: [],
  inviteKnown: false,
})

/** Same role line on search rows and venue checkboxes. */
export const personVenueRoleLabel = row => {
  if (!row) return ''
  if (row.owns) return 'They own it'
  if (row.onVenue) {
    return row.role ? getVenueRoleDisplayName(row.role) : 'No role recorded'
  }
  return 'Not on it'
}

/** Seed person-roles from a Remove Users search pick so labels match immediately. */
export const personRolesFromSearchPick = ({ venues, places } = {}) => {
  const on = {}
  for (const place of places || []) {
    const id = String(place.venueId || '')
    if (!id) continue
    on[id] = {
      onVenue: true,
      owns: Boolean(place.owns),
      role: String(place.role || ''),
    }
  }
  const rows = {}
  for (const venue of venues || []) {
    const id = String(venue._id)
    rows[id] = on[id] || { onVenue: false, owns: false, role: '' }
  }
  return rows
}

export const GROUP_PEOPLE_EMPTY = 'No one in this group matches.'

const PREVIEW_NOUNS = {
  allergens: ['allergen line', 'allergen lines'],
  restricted: ['restricted supplier', 'restricted suppliers'],
}

export const canReviewRestrictedSuppliers = ids => (ids || []).length > 0

/** Shown while Review invites stays disabled — same empty strictness as Recipes. */
export const INVITE_REVIEW_HINT = 'Pick people or enter an email.'

export const canReviewInvites = ({
  inviteChoice,
  peopleTicked,
  peopleVenueIds,
  inviteEmail,
  inviteVenueIds,
} = {}) => {
  if (inviteChoice === 'people') {
    return (peopleTicked || []).length > 0 && (peopleVenueIds || []).length > 0
  }
  if (inviteChoice === 'email') {
    return (
      String(inviteEmail || '').includes('@') &&
      (inviteVenueIds || []).length > 0
    )
  }
  return false
}

export const approvalEmailsFromText = text => [
  ...new Set(
    String(text || '')
      .split(/[,;\s]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email.includes('@')),
  ),
]

const countedPreview = (choice, preview) => {
  const nouns = PREVIEW_NOUNS[choice]
  if (!nouns || !preview) return null
  const count = Number(preview.count) || 0
  const noun = count === 1 ? nouns[0] : nouns[1]
  return { summary: `${count} ${noun}`, ...snapshotNames(preview.names, count) }
}

const ACTIVITY_LABELS = {
  green: 'Active',
  amber: 'Dormant',
  red: 'Inactive',
  purple: 'Never ordered',
}

export const ACTIVITY_INK = {
  green: '#237804',
  amber: '#d46b08',
  red: '#820014',
  purple: '#cf1322',
}

export const venueActivityLine = venue => {
  const label = ACTIVITY_LABELS[venue?.activityColor] || ''
  if (label === 'Never ordered') return 'Never ordered'
  const last = venue?.lastOrderDate ? `Last ordered ${venue.lastOrderDate}` : ''
  return [label, last].filter(Boolean).join(' · ')
}

const ACTIVITY_COUNT_ORDER = [
  ['green', 'active'],
  ['amber', 'dormant'],
  ['red', 'inactive'],
  ['purple', 'never ordered'],
]

export const groupActivityCounts = venues =>
  ACTIVITY_COUNT_ORDER.map(([color, label]) => {
    const count = (venues || []).filter(
      venue => venue?.activityColor === color,
    ).length
    return count > 0 ? { color, label, count, text: `${count} ${label}` } : null
  }).filter(Boolean)

export const groupActivityCountLine = venues =>
  groupActivityCounts(venues)
    .map(part => part.text)
    .join(' · ')

export const nextActivityFilter = (current, color) =>
  current === color ? 'all' : color

const quietOrderTime = value => {
  const match = String(value || '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!match) return null
  return new Date(
    Number(match[3]),
    Number(match[2]) - 1,
    Number(match[1]),
  ).getTime()
}

export const quietestVenue = venues => {
  const quiet = (venues || []).filter(
    venue =>
      venue?.activityColor === 'amber' ||
      venue?.activityColor === 'red' ||
      venue?.activityColor === 'purple',
  )
  if (!quiet.length) return null
  const ranked = [...quiet].sort((a, b) => {
    const aNever = a.activityColor === 'purple'
    const bNever = b.activityColor === 'purple'
    if (aNever !== bNever) return aNever ? -1 : 1
    const aTime = quietOrderTime(a.lastOrderDate)
    const bTime = quietOrderTime(b.lastOrderDate)
    if (aTime != null && bTime != null && aTime !== bTime) return aTime - bTime
    return String(a.title || '').localeCompare(String(b.title || ''), 'en', {
      sensitivity: 'base',
    })
  })
  return ranked[0]
}

export const quietestVenueLine = venues => {
  const venue = quietestVenue(venues)
  if (!venue) return ''
  if (venue.activityColor === 'purple' || !venue.lastOrderDate) {
    return `${venue.title} has never ordered.`
  }
  return `${venue.title} last ordered ${venue.lastOrderDate}.`
}

export const quietLineForList = (venues, listed, filtered) => {
  const line = quietestVenueLine(venues)
  if (!line || !filtered) return line
  const quiet = quietestVenue(venues)
  const id = String(quiet?._id || '')
  const present = (listed || []).some(venue =>
    id ? String(venue?._id) === id : venue?.title === quiet?.title,
  )
  return present ? line : ''
}

const venueGrossProfit = row => {
  const sales = Number(row?.totalSaleAmount) || 0
  const spend = (row?.salesPerDay || []).reduce(
    (sum, day) =>
      sum +
      (Number(day?.purchasesOnOMS) || 0) +
      (Number(day?.miscCostPerDay) || 0),
    0,
  )
  return sales - spend
}

export const groupSalesFigures = (payload, venueCount) => {
  const rows = Array.isArray(payload) ? payload : payload?.data || []
  const byId = {}
  let sales = 0
  let profit = 0
  rows.forEach(row => {
    const id = String(row?.venueTitle?._id || '')
    const venueSales = Number(row?.totalSaleAmount) || 0
    const venueProfit = venueGrossProfit(row)
    const gpp = row?.gpp == null || row?.gpp === '' ? null : Number(row.gpp)
    const figures = {
      sales: venueSales,
      gpp: Number.isFinite(gpp) ? gpp : null,
    }
    if (id) byId[id] = figures
    sales += venueSales
    profit += venueProfit
  })
  const withSales = rows.filter(row => Number(row?.totalSaleAmount) > 0).length
  const venues = Number.isFinite(Number(venueCount))
    ? Number(venueCount)
    : rows.length
  return {
    byId,
    sales,
    gpp: sales > 0 ? (profit / sales) * 100 : null,
    withSales,
    venues,
  }
}

const UK_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export const GROUP_SALES_PERIOD_OPTIONS = [
  { id: '7', label: 'Past 7 days', days: 7 },
  { id: '30', label: 'Past 30 days', days: 30 },
  { id: '90', label: 'Past 90 days', days: 90 },
]

const GROUP_SALES_PERIOD_KEY = 'oms.venueGroup.salesPeriod'

const londonDayParts = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const part = type => parts.find(item => item.type === type).value
  return {
    year: Number(part('year')),
    month: Number(part('month')),
    day: Number(part('day')),
  }
}

const isoUtcDay = date => {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${date.getUTCFullYear()}-${month}-${day}`
}

/** Inclusive window ending yesterday (UK), spanning `days` calendar days. */
export const groupSalesDayRange = (days = 30, now = new Date()) => {
  const span = Math.max(1, Number(days) || 30)
  const { year, month, day } = londonDayParts(now)
  const end = new Date(Date.UTC(year, month - 1, day))
  end.setUTCDate(end.getUTCDate() - 1)
  const start = new Date(end)
  start.setUTCDate(start.getUTCDate() - (span - 1))
  return { fromDate: isoUtcDay(start), toDate: isoUtcDay(end) }
}

export const pastThirtyDayRange = (now = new Date()) =>
  groupSalesDayRange(30, now)

export const readStoredGroupSalesPeriod = () => {
  try {
    const raw = window.localStorage.getItem(GROUP_SALES_PERIOD_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    if (parsed?.id === 'custom' && parsed.fromDate && parsed.toDate) {
      return {
        id: 'custom',
        fromDate: String(parsed.fromDate),
        toDate: String(parsed.toDate),
      }
    }
    if (GROUP_SALES_PERIOD_OPTIONS.some(option => option.id === parsed?.id)) {
      return { id: String(parsed.id) }
    }
  } catch (error) {
    // Private mode — fall back to the default window.
  }
  return { id: '30' }
}

export const writeStoredGroupSalesPeriod = period => {
  try {
    window.localStorage.setItem(
      GROUP_SALES_PERIOD_KEY,
      JSON.stringify(period || { id: '30' }),
    )
  } catch (error) {
    // ignore
  }
}

export const resolveGroupSalesRange = (period, now = new Date()) => {
  if (period?.id === 'custom' && period.fromDate && period.toDate) {
    return { fromDate: period.fromDate, toDate: period.toDate }
  }
  const option =
    GROUP_SALES_PERIOD_OPTIONS.find(row => row.id === period?.id) ||
    GROUP_SALES_PERIOD_OPTIONS.find(row => row.id === '30')
  return groupSalesDayRange(option.days, now)
}

export const groupSalesPeriodSelectValue = period =>
  period?.id === 'custom' ? 'custom' : period?.id || '30'

export const GROUP_SALES_FAILED = 'Sales could not be loaded.'

export const groupSalesProgressLine = (done, total, rangeLabel) => {
  const period = rangeLabel || 'Past 30 days'
  const finished = Math.max(0, Number(done) || 0)
  const count = Math.max(0, Number(total) || 0)
  return `${period} · ${finished} of ${count} venues`
}

export const loadGroupSalesInOrder = async (ids, fetchVenue, onVenue) => {
  const rows = []
  const failed = []
  const list = ids || []
  for (let index = 0; index < list.length; index += 1) {
    try {
      const body = await fetchVenue(list[index])
      const next = Array.isArray(body) ? body : body?.data || []
      rows.push(...next)
    } catch (error) {
      failed.push(list[index])
    }
    if (onVenue) onVenue(index + 1, list.length)
  }
  return { rows, failed }
}

export const salesFailedLine = (ids, venues) => {
  const names = (ids || [])
    .map(id => {
      const venue = (venues || []).find(
        item => String(item?._id) === String(id),
      )
      return venue?.title || ''
    })
    .filter(Boolean)
  if (!names.length) return ''
  if (names.length === 1) return `Sales for ${names[0]} could not be loaded.`
  if (names.length === 2)
    return `Sales for ${names[0]} and ${names[1]} could not be loaded.`
  return `Sales for ${names.slice(0, -1).join(', ')}, and ${
    names[names.length - 1]
  } could not be loaded.`
}

/**
 * UK sales window label. List column titles stay date-only (dense);
 * the group page can add a short “through end of yesterday” hint.
 */
export const formatUkSalesRange = (
  fromDate,
  toDate,
  { withHint = true } = {},
) => {
  const label = iso => {
    const [, month, day] = String(iso || '').split('-')
    return `${Number(day)} ${UK_MONTHS[Number(month) - 1]}`
  }
  if (!fromDate || !toDate) return 'Past 30 days'
  const period = `${label(fromDate)} – ${label(toDate)} ${String(toDate).slice(
    0,
    4,
  )}`
  return withHint ? `${period} · through end of yesterday` : period
}

export const formatGroupSales = amount => {
  const value = Number(amount) || 0
  return `£${value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

export const formatGroupGp = gpp => {
  if (gpp == null || !Number.isFinite(Number(gpp))) return ''
  return `${Number(gpp).toFixed(1)}% GP`
}

export const groupSalesHeaderLine = (summary, wholeGroup, rangeLabel) => {
  if (!summary) return ''
  const period = rangeLabel || 'Past 30 days'
  const lead = wholeGroup ? `Whole group, ${period}` : period
  const venues = Number(summary.venues) || 0
  const withSales = Number(summary.withSales) || 0
  const share = venues > 0 ? `from ${withSales} of ${venues} venues` : ''
  return [
    lead,
    `${formatGroupSales(summary.sales)} sales`,
    formatGroupGp(summary.gpp),
    share,
  ]
    .filter(Boolean)
    .join(' · ')
}

export const venueSalesLine = figures => {
  if (!figures) return ''
  if (!Number(figures.sales)) return 'No sales'
  return [formatGroupSales(figures.sales), formatGroupGp(figures.gpp)]
    .filter(Boolean)
    .join(' · ')
}

export const sortGroupVenuesBySales = (venues, byId, bySales) => {
  if (!bySales) return venues || []
  return [...(venues || [])].sort((a, b) => {
    const aSales = Number(byId?.[String(a?._id)]?.sales) || 0
    const bSales = Number(byId?.[String(b?._id)]?.sales) || 0
    if (aSales !== bSales) return bSales - aSales
    return String(a?.title || '').localeCompare(String(b?.title || ''), 'en', {
      sensitivity: 'base',
    })
  })
}

export const filterGroupVenues = (venues, query) => {
  const needle = String(query || '')
    .trim()
    .toLowerCase()
  if (!needle) return venues || []
  return (venues || []).filter(venue =>
    String(venue.title || '')
      .toLowerCase()
      .includes(needle),
  )
}

const VenueGroupPage = () => {
  const history = useHistory()
  const { groupId } = useParams()
  const location = useLocation()
  const account = useSelector(state => state.auth.account)
  const isOmsAdmin = account?.isAdmin === true
  const username = useSelector(state => state.auth.cognito?.username)
  const [group, setGroup] = useState(null)
  const [salesSummary, setSalesSummary] = useState(null)
  const [salesRange, setSalesRange] = useState(null)
  const [salesPeriod, setSalesPeriod] = useState(readStoredGroupSalesPeriod)
  const [salesLoading, setSalesLoading] = useState(false)
  const [salesDone, setSalesDone] = useState(0)
  const [salesMissed, setSalesMissed] = useState([])
  const [name, setName] = useState('')
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [duplicateName, setDuplicateName] = useState('')
  const [duplicateBusy, setDuplicateBusy] = useState(false)
  const [shareTargetIds, setShareTargetIds] = useState([])
  const [venueQuery, setVenueQuery] = useState('')
  const [memberPlan, setMemberPlan] = useState('all')
  const [memberBrand, setMemberBrand] = useState('all')
  const [memberActivity, setMemberActivity] = useState('all')
  const [sortBySales, setSortBySales] = useState(false)
  const [memberPage, setMemberPage] = useState(1)
  const [checked, setChecked] = useState([])
  const [addQuery, setAddQuery] = useState('')
  const [addResults, setAddResults] = useState([])
  const [pasteAmbiguous, setPasteAmbiguous] = useState([])
  const [pasteFeedback, setPasteFeedback] = useState('')
  const [planFilter, setPlanFilter] = useState('all')
  const [brandFilter, setBrandFilter] = useState('all')
  const [activityFilter, setActivityFilter] = useState('all')
  const [addPage, setAddPage] = useState(1)
  const [fillVenues, setFillVenues] = useState([])
  const [fillBrands, setFillBrands] = useState([])
  const [fillLoading, setFillLoading] = useState(false)
  const [addTruncated, setAddTruncated] = useState(false)
  const [pasted, setPasted] = useState('')
  const [toAdd, setToAdd] = useState([])
  const [listSource, setListSource] = useState('')
  const [listItems, setListItems] = useState([])
  const [listFileName, setListFileName] = useState('')
  const [listFileError, setListFileError] = useState('')
  const [listPreview, setListPreview] = useState(null)
  const [actionPreview, setActionPreview] = useState(null)
  const [listOpen, setListOpen] = useState(false)
  const [shareNote, setShareNote] = useState(null)
  const [fromProduct, setFromProduct] = useState([])
  const [toProduct, setToProduct] = useState([])
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [recipes, setRecipes] = useState([])
  const [recipesOpen, setRecipesOpen] = useState(false)
  const [menu, setMenu] = useState([])
  const [menuOpen, setMenuOpen] = useState(false)
  const [allergenSource, setAllergenSource] = useState('')
  const [allergensOpen, setAllergensOpen] = useState(false)
  const [restrictionSource, setRestrictionSource] = useState('')
  const [restrictionSuppliers, setRestrictionSuppliers] = useState([])
  const [restrictionTicked, setRestrictionTicked] = useState([])
  const [restrictionsOpen, setRestrictionsOpen] = useState(false)
  const [inviteChoice, setInviteChoice] = useState('')
  const [peopleSource, setPeopleSource] = useState('')
  const [venuePeople, setVenuePeople] = useState([])
  const [peopleTicked, setPeopleTicked] = useState([])
  const [peopleVenueIds, setPeopleVenueIds] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState(VENUE_ADMIN)
  const [inviteVenueIds, setInviteVenueIds] = useState([])
  const [inviteKnown, setInviteKnown] = useState(false)
  const inviteCheckBusy = useRef(false)
  const [invitesOpen, setInvitesOpen] = useState(false)
  const [personEmail, setPersonEmail] = useState('')
  const [personRole, setPersonRole] = useState('CHEF')
  const [personVenueIds, setPersonVenueIds] = useState([])
  const [personRoles, setPersonRoles] = useState({})
  const [groupPeopleQuery, setGroupPeopleQuery] = useState('')
  const [groupPeople, setGroupPeople] = useState([])
  const [groupPeopleReady, setGroupPeopleReady] = useState(false)
  const shareDestinations = (group?.venues || []).filter(
    venue => String(venue._id) !== String(peopleSource),
  )
  const [personOpen, setPersonOpen] = useState(false)
  const [revenueSource, setRevenueSource] = useState('')
  const [revenueCategories, setRevenueCategories] = useState([])
  const [revenueLoading, setRevenueLoading] = useState(false)
  const [revenueTicked, setRevenueTicked] = useState([])
  const [revenueNote, setRevenueNote] = useState('')
  const [revenueOpen, setRevenueOpen] = useState(false)
  const [copyChoice, setCopyChoice] = useState('')
  const [mismatchPage, setMismatchPage] = useState(0)
  const [openMismatch, setOpenMismatch] = useState(null)
  const [pageTab, setPageTab] = useState(GROUP_PAGE_TABS.venues)
  const [missesExpanded, setMissesExpanded] = useState(true)
  const [mismatchShareType, setMismatchShareType] = useState('all')
  const [clearingMismatches, setClearingMismatches] = useState(false)
  const [shareSelectOpen, setShareSelectOpen] = useState(false)

  /**
   * Share-type listbox vs miss panel: when the list opens, leave Misses, close
   * any miss dialog, and collapse the miss list so focus is not trapped under it.
   */
  const tuckMismatchesForShareSelect = open => {
    setShareSelectOpen(Boolean(open))
    if (!open) return
    setPageTab(GROUP_PAGE_TABS.share)
    setOpenMismatch(null)
    setMissesExpanded(false)
  }

  const openGroupTab = tabId => {
    setPageTab(tabId)
    if (tabId !== GROUP_PAGE_TABS.misses) return
    setMissesExpanded(mismatchesStartExpanded((group?.diverged || []).length))
    setMismatchPage(0)
  }

  const clearMismatchRows = async rows => {
    const list = rows || []
    if (!list.length) return
    setClearingMismatches(true)
    try {
      await Promise.all(
        list.map(row =>
          API.post('api2', `venue-groups/${groupId}/diverged/clear`, {
            body: {
              venue: row.venue,
              action: row.action,
              at: row.at,
            },
          }),
        ),
      )
      await load()
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message || 'Could not clear those rows.',
      })
    } finally {
      setClearingMismatches(false)
    }
  }

  const chooseCopyChoice = next => {
    setCopyChoice(next)
    const cleared = peopleShareDefaults()
    setPersonEmail(cleared.personEmail)
    setPersonRole(cleared.personRole)
    setPersonVenueIds(cleared.personVenueIds)
    setPersonRoles(cleared.personRoles)
    setPersonOpen(false)
    setGroupPeopleQuery(cleared.groupPeopleQuery)
    setGroupPeople(cleared.groupPeople)
    setGroupPeopleReady(cleared.groupPeopleReady)
    setInviteChoice(cleared.inviteChoice)
    setPeopleSource(cleared.peopleSource)
    setVenuePeople(cleared.venuePeople)
    setPeopleTicked(cleared.peopleTicked)
    setPeopleVenueIds(cleared.peopleVenueIds)
    setInviteEmail(cleared.inviteEmail)
    setInviteName(cleared.inviteName)
    setInviteRole(cleared.inviteRole)
    setInviteVenueIds(cleared.inviteVenueIds)
    setInviteKnown(cleared.inviteKnown)
    setInvitesOpen(false)
  }

  const load = useCallback(() => {
    return API.get('api2', `venue-groups/${groupId}`)
      .then(response => {
        const next = response?.data
        setGroup(next)
        setName(next?.name || '')
        setChecked([])
        return null
      })
      .catch(error => {
        RejectedNotification({
          message:
            error?.response?.data?.message || 'Could not open this group.',
        })
        history.replace('/venue-groups')
        return null
      })
  }, [groupId, history])

  const goToMissesTab = () => {
    setPageTab(GROUP_PAGE_TABS.misses)
    setMissesExpanded(true)
    setMismatchPage(0)
  }

  const rememberShare = note => {
    setShareNote(note)
    load()
    if (!shareResultsHaveMisses(note)) return
    goToMissesTab()
    const toastKey = 'group-share-view-misses'
    SuccessNotification({
      message: 'Some venues did not take this share.',
      duration: 8,
      key: toastKey,
      btn: (
        <Button size='small' onClick={goToMissesTab}>
          View misses
        </Button>
      ),
    })
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    if (params.get('tab') !== GROUP_MISSES_TAB_QUERY) return undefined
    goToMissesTab()
    return undefined
  }, [location.search, groupId])

  useEffect(() => {
    if (groupId === 'new') return undefined
    if (!canManageAnyVenue(account)) {
      history.replace('/venues')
      return undefined
    }
    load()
  }, [account, history, load, groupId])

  const chooseSalesPeriod = next => {
    writeStoredGroupSalesPeriod(next)
    setSalesPeriod(next)
  }

  useEffect(() => {
    const ids = (group?.venues || []).map(venue => venue._id).filter(Boolean)
    if (!ids.length) {
      setSalesSummary(null)
      setSalesMissed([])
      setSalesDone(0)
      return undefined
    }
    let cancelled = false
    const range = resolveGroupSalesRange(salesPeriod)
    setSalesRange(range)
    setSalesSummary(null)
    setSalesMissed([])
    setSalesDone(0)
    setSalesLoading(true)
    loadGroupSalesInOrder(
      ids,
      id =>
        API.post('api2', 'gross-profit/get-sales-data-for-venues', {
          body: {
            venues: [id],
            fromDate: range.fromDate,
            toDate: range.toDate,
          },
        }),
      done => {
        if (!cancelled) setSalesDone(done)
      },
    )
      .then(result => {
        if (!cancelled) {
          setSalesSummary(
            groupSalesFigures({ data: result?.rows || [] }, ids.length),
          )
          setSalesMissed(result?.failed || [])
        }
        return null
      })
      .catch(() => {
        if (!cancelled) setSalesMissed(ids)
        return null
      })
      .finally(() => {
        if (!cancelled) setSalesLoading(false)
      })
      .catch(() => null)
    return () => {
      cancelled = true
    }
  }, [group, salesPeriod])

  useEffect(() => {
    if (!addQuery.trim()) return undefined
    setAddResults([])
    setAddTruncated(false)
    setPasteFeedback('')
    setPasteAmbiguous([])
    return undefined
  }, [addQuery])

  useEffect(() => {
    if (copyChoice !== 'shopping' || !listSource || listItems.length) {
      setListPreview(null)
      return undefined
    }
    let cancelled = false
    API.get(
      'api2',
      `venue-groups/source-search?kind=shoppingList&venueId=${listSource}&q=`,
    )
      .then(response => {
        if (cancelled) return null
        setListPreview({
          count: Number(response?.count) || 0,
          names: (response?.data || []).map(row => row.title).filter(Boolean),
        })
        return null
      })
      .catch(() => {
        if (!cancelled) setListPreview(null)
        return null
      })
    return () => {
      cancelled = true
    }
  }, [copyChoice, listSource, listItems])

  useEffect(() => {
    if (inviteChoice !== 'people' || !peopleSource) {
      setVenuePeople([])
      return undefined
    }
    let cancelled = false
    API.get(
      'api2',
      `venue-groups/source-search?kind=people&venueId=${peopleSource}&q=`,
    )
      .then(response => {
        if (!cancelled) setVenuePeople(response?.data || [])
        return null
      })
      .catch(() => {
        if (!cancelled) setVenuePeople([])
        return null
      })
    return () => {
      cancelled = true
    }
  }, [inviteChoice, peopleSource])

  useEffect(() => {
    if (
      (copyChoice !== 'person' && copyChoice !== 'remove') ||
      !personEmail.includes('@')
    ) {
      setPersonRoles({})
      return undefined
    }
    let cancelled = false
    API.post('api2', `venue-groups/${groupId}/person-roles`, {
      body: { email: personEmail.trim() },
    })
      .then(response => {
        if (cancelled) return null
        const rows = {}
        for (const row of response?.data || []) rows[String(row.venueId)] = row
        setPersonRoles(rows)
        return null
      })
      .catch(() => {
        if (!cancelled) setPersonRoles({})
        return null
      })
    return () => {
      cancelled = true
    }
  }, [copyChoice, personEmail, groupId])

  useEffect(() => {
    if (copyChoice !== 'remove') {
      setGroupPeopleQuery('')
      setGroupPeople([])
      setGroupPeopleReady(false)
      return undefined
    }
    const q = groupPeopleQuery.trim()
    if (q.length < 2) {
      setGroupPeople([])
      setGroupPeopleReady(false)
      return undefined
    }
    let cancelled = false
    setGroupPeopleReady(false)
    const timer = setTimeout(() => {
      API.get(
        'api2',
        `venue-groups/${groupId}/people?q=${encodeURIComponent(q)}`,
      )
        .then(response => {
          if (cancelled) return null
          setGroupPeople(response?.data || [])
          setGroupPeopleReady(true)
          return null
        })
        .catch(() => {
          if (cancelled) return null
          setGroupPeople([])
          setGroupPeopleReady(true)
          return null
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [copyChoice, groupPeopleQuery, groupId])

  useEffect(() => {
    const venueByChoice = {
      allergens: allergenSource,
      restricted: restrictionSource,
    }
    const kindByChoice = {
      allergens: 'allergens',
      restricted: 'restrictedSuppliers',
    }
    const venueId = venueByChoice[copyChoice]
    const kind = kindByChoice[copyChoice]
    if (!venueId || !kind) {
      setActionPreview(null)
      if (copyChoice !== 'restricted') {
        setRestrictionSuppliers([])
        setRestrictionTicked([])
      }
      return undefined
    }
    let cancelled = false
    const previewRequest = API.get(
      'api2',
      `venue-groups/source-search?kind=${kind}&venueId=${venueId}&q=`,
    )
      .then(response => {
        if (cancelled) return null
        const rows = response?.data || []
        setActionPreview({
          count: Number(response?.count) || 0,
          names: rows.map(row => row.title).filter(Boolean),
        })
        if (kind === 'restrictedSuppliers') {
          const suppliers = rows
            .filter(row => row._id)
            .map(row => ({
              ...row,
              emails: (row.approvalEmails || []).join(', '),
            }))
          setRestrictionSuppliers(suppliers)
          setRestrictionTicked(suppliers.map(row => String(row._id)))
        }
        return null
      })
      .catch(() => {
        if (!cancelled) setActionPreview(null)
        return null
      })
    previewRequest.catch(() => null)
    return () => {
      cancelled = true
    }
  }, [copyChoice, allergenSource, restrictionSource])

  useEffect(() => {
    if (copyChoice !== 'revenue' || !revenueSource) {
      setRevenueCategories([])
      setRevenueTicked([])
      setRevenueLoading(false)
      return undefined
    }
    let cancelled = false
    setRevenueLoading(true)
    setRevenueCategories([])
    setRevenueTicked([])
    listRevenueCategories(revenueSource)
      .then(rows => {
        if (cancelled) return null
        const active = activeRevenueCategories(rows)
        setRevenueCategories(active)
        setRevenueTicked(active.map(category => String(category._id)))
        setRevenueLoading(false)
        return null
      })
      .catch(() => {
        if (!cancelled) {
          setRevenueCategories([])
          setRevenueTicked([])
          setRevenueLoading(false)
        }
        return null
      })
    return () => {
      cancelled = true
    }
  }, [copyChoice, revenueSource])

  useEffect(() => {
    if (
      copyChoice !== 'revenue' ||
      !revenueSource ||
      revenueTicked.length === 0
    ) {
      setRevenueNote('')
      return undefined
    }
    let cancelled = false
    API.post('api2', `venue-groups/${groupId}/runs/preview`, {
      body: {
        action: 'assign_revenue_categories',
        payload: { sourceVenueId: revenueSource, categoryIds: revenueTicked },
      },
    })
      .then(response => {
        if (!cancelled)
          setRevenueNote(response?.data?.revenuePreview?.note || '')
        return null
      })
      .catch(() => {
        if (!cancelled) setRevenueNote('')
        return null
      })
    return () => {
      cancelled = true
    }
  }, [copyChoice, groupId, revenueSource, revenueTicked])

  const fillActive =
    Boolean(addQuery.trim()) ||
    planFilter !== 'all' ||
    (brandFilter !== 'all' && isOmsAdmin) ||
    activityFilter !== 'all'
  const memberFilterActive =
    memberPlan !== 'all' ||
    (memberBrand !== 'all' && isOmsAdmin) ||
    memberActivity !== 'all'
  const needsCatalogue = Boolean(username)

  useEffect(() => {
    if (!needsCatalogue || !username) {
      setFillLoading(false)
      return undefined
    }
    let cancelled = false
    setFillLoading(true)
    const catalogue = Promise.all([
      fetchEveryVenuePage({
        username,
        get: API.get,
      }),
      API.get('api2', 'venue-groups/venue-brands'),
    ])
      .then(([list, brands]) => {
        if (cancelled) return null
        setFillVenues(list || [])
        setFillBrands(brands?.data || [])
        return null
      })
      .catch(() => {
        if (!cancelled) setFillVenues([])
        return null
      })
      .finally(() => {
        if (!cancelled) setFillLoading(false)
      })
    catalogue.catch(() => null)
    return () => {
      cancelled = true
    }
  }, [needsCatalogue, username])

  useEffect(() => {
    setToAdd([])
    setAddPage(1)
  }, [planFilter, brandFilter, activityFilter, addQuery])

  useEffect(() => {
    setChecked([])
    setMemberPage(1)
  }, [memberPlan, memberBrand, memberActivity, venueQuery, sortBySales])

  useEffect(() => {
    setShareTargetIds(current => syncShareTargetIds(current, group?.venues))
  }, [group?.venues])

  const rename = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      const response = await API.patch('api2', `venue-groups/${groupId}`, {
        body: { name: trimmed },
      })
      SuccessNotification({ message: 'Group renamed.' })
      if (response?.warning) WarningNotification({ message: response.warning })
      await load()
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message || 'Could not rename this group.',
      })
    }
  }

  const addVenues = async venueIds => {
    const ids = [...new Set(venueIds.map(String))]
    if (ids.length === 0) return
    try {
      await API.post('api2', `venue-groups/${groupId}/venues`, {
        body: { venueIds: ids },
      })
      SuccessNotification({ message: 'Venues added.' })
      setToAdd([])
      setPasted('')
      await load()
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message || 'Could not add those venues.',
      })
    }
  }

  const findPasted = async () => {
    if (!pasted.trim()) return
    try {
      const response = await API.post('api2', 'venue-groups/venue-search', {
        body: { names: pasted },
      })
      const matches = response?.data || []
      const grouped = matchPastedVenueNames(matches, pasted)
      const alreadyInGroup = new Set(
        (group?.venues || []).map(venue => String(venue._id)),
      )
      const allowedMatched = (grouped.matched || []).filter(
        venue =>
          venue.canChange !== false && !alreadyInGroup.has(String(venue._id)),
      )
      const jumpTo = venuesForPasteJump({
        matched: allowedMatched,
        ambiguous: grouped.ambiguous,
      })
      const addPageSizeForPaste = 20
      const onPage = Math.min(allowedMatched.length, addPageSizeForPaste)
      setAddQuery('')
      setPlanFilter('all')
      setBrandFilter('all')
      setActivityFilter('all')
      setAddResults(jumpTo.length ? jumpTo : matches)
      setAddTruncated(response?.truncated === true)
      setPasteAmbiguous(ambiguousPasteLines(grouped.ambiguous))
      setAddPage(1)
      setPasteFeedback(
        pasteSelectionFeedback({
          selectedCount: allowedMatched.length,
          visibleOnPageCount: onPage,
        }),
      )
      setToAdd(current =>
        selectAllowedVenueMatches(current, allowedMatched, allowedMatched),
      )
      if ((response?.unmatched || []).length) {
        RejectedNotification({
          message: `Not found: ${response.unmatched.join(', ')}`,
        })
      }
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message || 'Could not find those venues.',
      })
    }
  }

  const removeVenues = async () => {
    if (checked.length === 0) return
    try {
      await API.post('api2', `venue-groups/${groupId}/venues/remove`, {
        body: { venueIds: checked },
      })
      SuccessNotification({ message: 'Venues removed.' })
      await load()
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message || 'Could not remove those venues.',
      })
    }
  }

  const archive = () => {
    Modal.confirm({
      title: 'Archive this group?',
      content:
        'The group is hidden. Nothing already shared with its venues is undone.',
      okText: 'Archive',
      onOk: async () => {
        await API.post('api2', `venue-groups/${groupId}/archive`, { body: {} })
        history.push('/venue-groups')
      },
    })
  }

  const openDuplicate = () => {
    setDuplicateName(suggestedDuplicateGroupName(group?.name || name))
    setDuplicateOpen(true)
  }

  const confirmDuplicate = async () => {
    const trimmed = duplicateName.trim()
    if (!trimmed || duplicateBusy) return
    setDuplicateBusy(true)
    try {
      const response = await API.post(
        'api2',
        `venue-groups/${groupId}/duplicate`,
        { body: { name: trimmed } },
      )
      const nextId = response?.data?._id
      SuccessNotification({ message: 'Group duplicated.' })
      if (response?.warning) WarningNotification({ message: response.warning })
      setDuplicateOpen(false)
      if (nextId) history.push(`/venue-groups/${nextId}`)
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message || 'Could not duplicate this group.',
      })
    } finally {
      setDuplicateBusy(false)
    }
  }

  const personSelectAllIds = personSelectAllVenueIds({
    venues: group?.venues,
    personRoles,
    removing: copyChoice === 'remove',
  })

  if (!canManageAnyVenue(account) || !group) return null

  const filteredMismatches = filterMismatchesByShareType(
    group.diverged,
    mismatchShareType,
  )

  const canEditMeta = canEditVenueGroupMeta(account, group)
  const usesOwnVenuePicker =
    GROUP_SHARE_USES_OWN_VENUE_PICKER.includes(copyChoice)
  const showShareTargetPicker =
    !usesOwnVenuePicker && (group.venues || []).length > 1
  const shareTargetsReady = usesOwnVenuePicker || shareTargetIds.length > 0
  const withShareTargets = payload =>
    sharePayloadWithTargets(payload, shareTargetIds, group.venues)
  const brandById = new Map(
    (fillBrands || []).map(venue => [String(venue._id), venue.brand || '']),
  )
  const catalogueById = new Map(
    (fillVenues || []).map(venue => [String(venue._id), venue]),
  )
  const memberVenues = enrichGroupMemberVenues(group.venues, catalogueById)
  const memberLastRunLines = lastRunLinesForMembers(
    group.lastRun?.lines,
    group.venues,
  )
  const activityCountLine = groupActivityCountLine(memberVenues)
  const listFiltered =
    memberFilterActive || Boolean(String(venueQuery || '').trim())
  const filteredMembers = filterGroupVenues(
    memberFilterActive
      ? filterVenuesForGroupFill(memberVenues, {
          plan: memberPlan,
          brand: isOmsAdmin ? memberBrand : 'all',
          activity: memberActivity,
          brands: brandById,
        })
      : memberVenues,
    venueQuery,
  )
  const orderedMembers = sortGroupVenuesBySales(
    filteredMembers,
    salesSummary?.byId,
    sortBySales,
  )
  const quietVenue = quietestVenue(memberVenues)
  const quietLine = quietLineForList(
    memberVenues,
    filteredMembers,
    listFiltered,
  )
  const memberPageSize = 10
  const memberPageCount = Math.max(
    1,
    Math.ceil(orderedMembers.length / memberPageSize),
  )
  const safeMemberPage = Math.min(memberPage, memberPageCount)
  const shown = orderedMembers.slice(
    (safeMemberPage - 1) * memberPageSize,
    safeMemberPage * memberPageSize,
  )
  const memberRangeStart =
    filteredMembers.length === 0 ? 0 : (safeMemberPage - 1) * memberPageSize + 1
  const memberRangeEnd = Math.min(
    safeMemberPage * memberPageSize,
    filteredMembers.length,
  )
  const listRun = shoppingListRunPayload(listSource, listItems)
  const snapshot = shareSnapshot({
    choice: copyChoice,
    csvItems: listItems,
    listPreview,
    recipes,
    menu,
    people: [],
    personEmail,
    personRole,
    actionPreview,
  })
  const memberIds = new Set(
    (group.venues || []).map(venue => String(venue._id)),
  )
  const candidateVenues = fillActive
    ? filterGroupVenues(
        filterVenuesForGroupFill(fillVenues, {
          plan: planFilter,
          brand: isOmsAdmin ? brandFilter : 'all',
          activity: activityFilter,
          brands: brandById,
        }),
        addQuery,
      )
    : addResults
  const addableVenues = candidateVenues.filter(
    venue => !memberIds.has(String(venue._id)) && venue.canChange !== false,
  )
  const addPageSize = 20
  const addPageCount = Math.max(
    1,
    Math.ceil(addableVenues.length / addPageSize),
  )
  const safeAddPage = Math.min(addPage, addPageCount)
  const pageAddList = fillActive || addResults.length > 0
  const shownAddVenues = pageAddList
    ? addableVenues.slice(
        (safeAddPage - 1) * addPageSize,
        safeAddPage * addPageSize,
      )
    : addableVenues
  const addRangeStart =
    addableVenues.length === 0 ? 0 : (safeAddPage - 1) * addPageSize + 1
  const addRangeEnd = Math.min(safeAddPage * addPageSize, addableVenues.length)
  const chosenToAdd = toAdd.filter(venue =>
    addableVenues.some(match => String(match._id) === String(venue._id)),
  )
  const confirmRemove = () => {
    const count = checked.length
    if (!count) return
    const noun = count === 1 ? 'venue' : 'venues'
    Modal.confirm({
      title: `Remove ${count} ${noun} from ${group.name}?`,
      content: 'They leave the group. Anything already copied to them stays.',
      okText: 'Remove venues',
      okType: 'danger',
      onOk: () => removeVenues(),
    })
  }

  const confirmAddAll = () => {
    const count = addableVenues.length
    if (!count) return
    const noun = count === 1 ? 'venue' : 'venues'
    Modal.confirm({
      title: `Add ${count} ${noun} to ${group.name}?`,
      content:
        'Every venue in this filter joins the group. Earlier shares are not replayed.',
      okText: 'Add venues',
      onOk: () => addVenues(addableVenues.map(venue => venue._id)),
    })
  }

  const confirmAdd = () => {
    const count = chosenToAdd.length
    if (!count) return
    const noun = count === 1 ? 'venue' : 'venues'
    Modal.confirm({
      title: `Add ${count} ${noun} to ${group.name}?`,
      content:
        'They join this group from now on. Earlier shares are not replayed.',
      okText: 'Add venues',
      onOk: () => addVenues(chosenToAdd.map(venue => venue._id)),
    })
  }

  return (
    <FadeIn>
      <GroupPage $wide>
        <GroupHero>
          <NameRow>
            <NameInput
              aria-label='Group name'
              value={name}
              maxLength={80}
              disabled={!canEditMeta}
              onChange={event => setName(event.target.value)}
            />
            {canEditMeta ? (
              <Button type='primary' disabled={!name.trim()} onClick={rename}>
                Save name
              </Button>
            ) : null}
            <QuietButton onClick={openDuplicate}>Duplicate</QuietButton>
          </NameRow>
          {isOmsAdmin ? (
            <Checkbox
              checked={group.customerVisible === false}
              onChange={async event => {
                const next = !event.target.checked
                try {
                  const response = await API.patch(
                    'api2',
                    `venue-groups/${groupId}/customer-visible`,
                    { body: { customerVisible: next } },
                  )
                  // PATCH returns a lean group (venue ids only). Keep the
                  // populated member list and only flip visibility.
                  setGroup(current => ({
                    ...current,
                    customerVisible:
                      response?.data?.customerVisible != null
                        ? response.data.customerVisible
                        : next,
                  }))
                  SuccessNotification({
                    message: next
                      ? 'Customers can see this group again.'
                      : 'Hidden from customers (OMS only).',
                  })
                } catch (error) {
                  RejectedNotification({
                    message:
                      error?.response?.data?.message ||
                      'Could not update customer visibility.',
                  })
                }
              }}
            >
              OMS only — hide from customers
            </Checkbox>
          ) : null}
          <RunLine>{formatGroupLastRun(group.lastRun)}</RunLine>
          <GroupLead>{groupVenuesLead((group.venues || []).length)}</GroupLead>
          {activityCountLine ? (
            <RunLine>
              {groupActivityCounts(memberVenues).map((part, index) => (
                <span key={part.color}>
                  {index > 0 ? ' · ' : null}
                  <CountButton
                    type='button'
                    $pressed={memberActivity === part.color}
                    $ink={ACTIVITY_INK[part.color]}
                    aria-pressed={memberActivity === part.color}
                    onClick={() =>
                      setMemberActivity(
                        nextActivityFilter(memberActivity, part.color),
                      )
                    }
                  >
                    <ActivityDot $color={ACTIVITY_INK[part.color]} />
                    {part.text}
                  </CountButton>
                </span>
              ))}
            </RunLine>
          ) : null}
          {quietLine ? (
            <RunLine $ink={ACTIVITY_INK[quietVenue.activityColor]}>
              {quietLine}
            </RunLine>
          ) : null}
          <FilterRow>
            <FilterField label='Sales period'>
              <Select
                value={groupSalesPeriodSelectValue(salesPeriod)}
                onChange={value => {
                  if (value === 'custom') {
                    chooseSalesPeriod({
                      id: 'custom',
                      fromDate:
                        salesPeriod?.fromDate ||
                        salesRange?.fromDate ||
                        pastThirtyDayRange().fromDate,
                      toDate:
                        salesPeriod?.toDate ||
                        salesRange?.toDate ||
                        pastThirtyDayRange().toDate,
                    })
                    return
                  }
                  chooseSalesPeriod({ id: value })
                }}
                style={{ width: 160 }}
              >
                {GROUP_SALES_PERIOD_OPTIONS.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
                <Select.Option value='custom'>Custom dates</Select.Option>
              </Select>
            </FilterField>
            {salesPeriod?.id === 'custom' ? (
              <DatePicker.RangePicker
                allowClear={false}
                value={[
                  salesPeriod.fromDate ? moment(salesPeriod.fromDate) : null,
                  salesPeriod.toDate ? moment(salesPeriod.toDate) : null,
                ]}
                onChange={dates => {
                  if (!dates?.[0] || !dates?.[1]) return
                  chooseSalesPeriod({
                    id: 'custom',
                    fromDate: dates[0].format('YYYY-MM-DD'),
                    toDate: dates[1].format('YYYY-MM-DD'),
                  })
                }}
              />
            ) : null}
          </FilterRow>
          {salesLoading ? (
            <RunLine>
              {groupSalesProgressLine(
                salesDone,
                (group.venues || []).length,
                formatUkSalesRange(salesRange?.fromDate, salesRange?.toDate),
              )}
            </RunLine>
          ) : null}
          {!salesLoading && salesFailedLine(salesMissed, group.venues) ? (
            <RunLine>{salesFailedLine(salesMissed, group.venues)}</RunLine>
          ) : null}
          {!salesLoading && salesSummary ? (
            <RunLine>
              {groupSalesHeaderLine(
                salesSummary,
                listFiltered,
                formatUkSalesRange(salesRange?.fromDate, salesRange?.toDate),
              )}
            </RunLine>
          ) : null}
          {(group.lastRun?.action === 'copy_restricted_suppliers'
            ? restrictedSupplierReceiptLines(memberLastRunLines)
            : group.lastRun?.action === 'apply_shopping_list'
            ? shoppingListReceiptLines(
                memberLastRunLines.map(line => ({
                  venueTitle: line.title,
                  outcome: line.outcome,
                  reason: line.reason,
                  supplierNames: line.supplierNames,
                  skippedLines: line.skippedLines,
                })),
              ).filter(line => !line.endsWith(' updated.'))
            : receiptProblemLines(
                memberLastRunLines.map(line => ({
                  venueTitle: line.title,
                  recipeTitle: line.recipe,
                  outcome: line.outcome,
                  reason: line.reason,
                })),
              )
          ).map(line => (
            <RunLine key={line}>{line}</RunLine>
          ))}
          <BackLink to='/venue-groups'>Back to groups</BackLink>
        </GroupHero>
        <GroupSubnav aria-label='Group sections'>
          {groupPageTabItems((group.diverged || []).length).map(tab => (
            <GroupSubnavTab
              key={tab.id}
              type='button'
              aria-current={pageTab === tab.id ? 'page' : undefined}
              $active={pageTab === tab.id}
              onClick={() => openGroupTab(tab.id)}
            >
              {tab.label}
            </GroupSubnavTab>
          ))}
        </GroupSubnav>
        {pageTab === GROUP_PAGE_TABS.misses && (
          <div aria-hidden={shareSelectOpen ? 'true' : undefined}>
            <GroupSurface title='No longer match'>
              {(group.diverged || []).length === 0 ? (
                <Reason>
                  Nothing to deal with. Misses show up here after a share that
                  did not land.
                </Reason>
              ) : (
                <>
                  <Reason>
                    Shown after a share, when a venue did not take it. Open a
                    row for the issue and a fix. Clear a row when you have dealt
                    with it, or you do not want to see it. It comes back only if
                    a later share misses again.
                  </Reason>
                  <FilterRow>
                    <FilterField label='Share type'>
                      <Select
                        value={mismatchShareType}
                        onChange={value => {
                          setMismatchShareType(value)
                          setMismatchPage(0)
                        }}
                        style={{ width: 200 }}
                      >
                        <Select.Option value='all'>
                          All share types
                        </Select.Option>
                        {mismatchShareTypeOptions(group.diverged).map(
                          option => (
                            <Select.Option
                              key={option.action}
                              value={option.action}
                            >
                              {option.label}
                            </Select.Option>
                          ),
                        )}
                      </Select>
                    </FilterField>
                    <QuietButton
                      disabled={
                        filteredMismatches.length === 0 || clearingMismatches
                      }
                      onClick={() => clearMismatchRows(filteredMismatches)}
                    >
                      Clear all dealt-with
                    </QuietButton>
                  </FilterRow>
                  {filteredMismatches.length === 0 ? (
                    <Reason>No misses for this share type.</Reason>
                  ) : !missesExpanded ? (
                    <QuietButton
                      type='button'
                      onClick={() => setMissesExpanded(true)}
                    >
                      {`Show ${filteredMismatches.length} ${
                        filteredMismatches.length === 1 ? 'miss' : 'misses'
                      }`}
                    </QuietButton>
                  ) : (
                    <>
                      {(group.diverged || []).length > MISMATCH_COLLAPSE_AT ? (
                        <QuietButton
                          type='button'
                          onClick={() => setMissesExpanded(false)}
                        >
                          Hide misses
                        </QuietButton>
                      ) : null}
                      <VenueList>
                        {mismatchSlice(
                          filteredMismatches,
                          mismatchPage,
                        ).rows.map(row => (
                          <MismatchRow
                            key={`${row.venue}-${row.action}-${
                              row.at || row.reason
                            }`}
                          >
                            <MismatchButton
                              type='button'
                              onClick={() => setOpenMismatch(row)}
                            >
                              <span>{row.title || 'Venue'}</span>
                              <Glance>{mismatchGlance(row)}</Glance>
                              <QuietMark>{row.label}</QuietMark>
                            </MismatchButton>
                            <Button
                              disabled={clearingMismatches}
                              onClick={() => clearMismatchRows([row])}
                            >
                              Clear
                            </Button>
                          </MismatchRow>
                        ))}
                      </VenueList>
                      {mismatchSlice(filteredMismatches, mismatchPage).pages >
                        1 && (
                        <FilterRow>
                          <Button
                            disabled={
                              mismatchSlice(filteredMismatches, mismatchPage)
                                .page === 0
                            }
                            onClick={() => setMismatchPage(page => page - 1)}
                          >
                            Previous page
                          </Button>
                          <span>
                            {mismatchSlice(filteredMismatches, mismatchPage)
                              .page + 1}{' '}
                            of{' '}
                            {
                              mismatchSlice(filteredMismatches, mismatchPage)
                                .pages
                            }
                          </span>
                          <Button
                            disabled={
                              mismatchSlice(filteredMismatches, mismatchPage)
                                .page >=
                              mismatchSlice(filteredMismatches, mismatchPage)
                                .pages -
                                1
                            }
                            onClick={() => setMismatchPage(page => page + 1)}
                          >
                            Next page
                          </Button>
                        </FilterRow>
                      )}
                    </>
                  )}
                  <Modal
                    visible={Boolean(openMismatch) && !shareSelectOpen}
                    title={
                      openMismatch
                        ? `${openMismatch.title || 'Venue'} · ${
                            openMismatch.label || 'Share'
                          }`
                        : ''
                    }
                    footer={null}
                    keyboard
                    destroyOnClose
                    maskClosable
                    onCancel={() => setOpenMismatch(null)}
                  >
                    {openMismatch && (
                      <>
                        <p>{mismatchAdvice(openMismatch).issue}</p>
                        <p>{mismatchAdvice(openMismatch).fix}</p>
                      </>
                    )}
                  </Modal>
                </>
              )}
            </GroupSurface>
          </div>
        )}
        {pageTab === GROUP_PAGE_TABS.venues && (
          <GroupSurface title='Venues'>
            <FilterCaption>{GROUP_MEMBERS_CAPTION}</FilterCaption>
            <FilterRow>
              <FilterField label='Name' $grow>
                <Input
                  value={venueQuery}
                  onChange={event => setVenueQuery(event.target.value)}
                  placeholder='Name contains'
                />
              </FilterField>
              <FilterField label='Plan'>
                <Select
                  value={memberPlan}
                  onChange={setMemberPlan}
                  style={{ width: 140 }}
                >
                  <Select.Option value='all'>All plans</Select.Option>
                  <Select.Option value='Free Plan'>Free</Select.Option>
                  <Select.Option value='Starter Plan'>Starter</Select.Option>
                  <Select.Option value='Growth Plan'>Growth</Select.Option>
                  <Select.Option value='Pro Plan'>Pro</Select.Option>
                </Select>
              </FilterField>
              {isOmsAdmin && (
                <FilterField label='Brand'>
                  <Select
                    value={memberBrand}
                    onChange={setMemberBrand}
                    style={{ width: 140 }}
                  >
                    <Select.Option value='all'>All brands</Select.Option>
                    <Select.Option value='TFR'>TFR</Select.Option>
                    <Select.Option value='GREEN'>GREEN</Select.Option>
                    <Select.Option value='JUST'>JUST</Select.Option>
                  </Select>
                </FilterField>
              )}
              <FilterField label='Activity'>
                <ActivitySelect
                  value={memberActivity}
                  onChange={setMemberActivity}
                />
              </FilterField>
            </FilterRow>
            {memberFilterActive && fillLoading && (
              <Reason>Loading venues…</Reason>
            )}
            {(memberFilterActive || filteredMembers.length > memberPageSize) &&
              !fillLoading && (
                <Reason>
                  {filteredMembers.length} in this group
                  {filteredMembers.length > memberPageSize
                    ? `. Showing ${memberRangeStart}–${memberRangeEnd}.`
                    : '.'}
                </Reason>
              )}
            {memberPageCount > 1 && (
              <Row>
                <Button
                  disabled={safeMemberPage <= 1}
                  onClick={() => setMemberPage(page => Math.max(1, page - 1))}
                >
                  Previous page
                </Button>
                <Button
                  disabled={safeMemberPage >= memberPageCount}
                  onClick={() => setMemberPage(page => page + 1)}
                >
                  Next page
                </Button>
              </Row>
            )}
            <VenueList>
              {shown.length > 0 ? (
                <SalesHead>
                  <span />
                  <SalesHeadText>
                    Sales
                    <SortSales
                      type='button'
                      $pressed={sortBySales}
                      aria-pressed={sortBySales}
                      onClick={() => setSortBySales(current => !current)}
                    >
                      {sortBySales ? 'Highest first' : 'Sort by sales'}
                    </SortSales>
                  </SalesHeadText>
                  <span />
                </SalesHead>
              ) : null}
              {shown.map(venue => (
                <MemberRow key={venue._id}>
                  <label>
                    <input
                      type='checkbox'
                      checked={checked.includes(String(venue._id))}
                      onChange={() =>
                        setChecked(current =>
                          current.includes(String(venue._id))
                            ? current.filter(id => id !== String(venue._id))
                            : [...current, String(venue._id)],
                        )
                      }
                    />
                    <span>
                      {venue.title}
                      {venueActivityLine(venue) ? (
                        <ActivityLine $ink={ACTIVITY_INK[venue.activityColor]}>
                          {venueActivityLine(venue)}
                        </ActivityLine>
                      ) : null}
                    </span>
                  </label>
                  <SalesCell>
                    {(() => {
                      const missed = salesMissed
                        .map(String)
                        .includes(String(venue._id))
                      const salesText = missed
                        ? 'Could not be loaded'
                        : venueSalesLine(
                            salesSummary?.byId?.[String(venue._id)],
                          )
                      const salesPath = venueGrossProfitPath(
                        venue._id,
                        salesRange,
                      )
                      if (!salesText) return null
                      if (!salesPath) return salesText
                      return (
                        <SalesLink
                          to={salesPath}
                          aria-label={`Gross profit for ${venue.title}`}
                        >
                          {salesText}
                        </SalesLink>
                      )
                    })()}
                  </SalesCell>
                  <VenueLinks>
                    <Link
                      data-chrome='quiet'
                      to={`/v/${venue._id}/products`}
                      aria-label={`Shopping list for ${venue.title}`}
                    >
                      Shopping list
                    </Link>
                    <Link
                      data-chrome='quiet'
                      to={`/v/${venue._id}/menu-builder`}
                      aria-label={`Recipes for ${venue.title}`}
                    >
                      Recipes
                    </Link>
                    <Link
                      data-chrome='quiet'
                      to={`/v/${venue._id}/settings`}
                      aria-label={`Settings for ${venue.title}`}
                    >
                      Settings
                    </Link>
                  </VenueLinks>
                </MemberRow>
              ))}
            </VenueList>
            <Row>
              <QuietButton
                disabled={
                  filteredMembers.length === 0 ||
                  (memberFilterActive && fillLoading)
                }
                onClick={() =>
                  setChecked(filteredMembers.map(venue => String(venue._id)))
                }
              >
                {`Select all venues (${filteredMembers.length})`}
              </QuietButton>
              <QuietButton
                disabled={checked.length === 0}
                onClick={() => setChecked([])}
              >
                Deselect all
              </QuietButton>
              <QuietButton
                disabled={checked.length === 0}
                onClick={confirmRemove}
              >
                Remove selected
              </QuietButton>
            </Row>
          </GroupSurface>
        )}
        {pageTab === GROUP_PAGE_TABS.share && (
          <GroupSurface title='Manage venues in this group'>
            <Reason>{GROUP_SHARE_LEAD}</Reason>
            <Reason>{GROUP_SHARE_SUBSET_TIP}</Reason>
            <CopyStep
              step='1'
              title='Choose sharing option'
              line='Pick one thing from the list.'
            >
              <FieldSelect
                id='copy-choice'
                ariaLabel='Choose sharing option'
                value={copyChoice}
                onChange={chooseCopyChoice}
                onDropdownVisibleChange={tuckMismatchesForShareSelect}
                placeholder='Choose'
              >
                <Select.OptGroup label='Menu'>
                  {copyChoiceOption(
                    'recipes',
                    'Recipes',
                    'Copies the recipes you pick. A dish missing an ingredient is left out.',
                  )}
                  {copyChoiceOption(
                    'menu',
                    'Menu',
                    'Copies this menu, its sections, and the dishes on it.',
                  )}
                  {copyChoiceOption(
                    'allergens',
                    'Allergens',
                    'Copies allergen flags onto the same products.',
                  )}
                </Select.OptGroup>
                <Select.OptGroup label='Ordering'>
                  {copyChoiceOption(
                    'shopping',
                    'Shopping list',
                    SHOPPING_LIST_REPLACES,
                  )}
                  {copyChoiceOption(
                    'replace',
                    'Replace a product',
                    'Replaces one product with another.',
                  )}
                  {copyChoiceOption(
                    'restricted',
                    'Restricted suppliers',
                    'Copies which suppliers are restricted, and who approves them.',
                  )}
                </Select.OptGroup>
                <Select.OptGroup label='Gross profit'>
                  {copyChoiceOption(
                    'revenue',
                    'Revenue categories',
                    'Replaces the revenue categories on the other venues. It does not copy the sales amounts.',
                  )}
                </Select.OptGroup>
                <Select.OptGroup label='People'>
                  {copyChoiceOption(
                    'invites',
                    'Invite Users',
                    'Invites these people to each venue.',
                  )}
                  {copyChoiceOption(
                    'remove',
                    'Remove Users',
                    'Takes this person off the venues you tick. One email covers the removal.',
                  )}
                  {copyChoiceOption(
                    'person',
                    'Change a person’s role',
                    'Changes the role this person already has.',
                  )}
                </Select.OptGroup>
              </FieldSelect>
            </CopyStep>
            {showShareTargetPicker ? (
              <CopyStep
                step='2'
                title='Send to'
                line='Tick the venues this share should reach. Leave them all ticked for the whole group.'
              >
                <Row>
                  <QuietButton
                    disabled={(group.venues || []).length === 0}
                    onClick={() =>
                      setShareTargetIds(
                        (group.venues || []).map(venue => String(venue._id)),
                      )
                    }
                  >
                    {`Select all (${(group.venues || []).length})`}
                  </QuietButton>
                  <QuietButton
                    disabled={shareTargetIds.length === 0}
                    onClick={() => setShareTargetIds([])}
                  >
                    Deselect all
                  </QuietButton>
                </Row>
                {(group.venues || []).map(venue => {
                  const id = String(venue._id)
                  return (
                    <label key={id} style={{ display: 'block', marginTop: 8 }}>
                      <input
                        type='checkbox'
                        checked={shareTargetIds.includes(id)}
                        onChange={event => {
                          setShareTargetIds(current =>
                            event.target.checked
                              ? [...current, id]
                              : current.filter(ticked => ticked !== id),
                          )
                        }}
                      />{' '}
                      {venue.title || 'Venue'}
                    </label>
                  )
                })}
              </CopyStep>
            ) : null}
            <CopyStep
              step={showShareTargetPicker ? '3' : '2'}
              title='Check it'
              line='Set where it comes from, and read the note before you send.'
            >
              {copyChoice === 'shopping' && (
                <>
                  <VenueSourceSelect
                    id='list-source'
                    venues={group.venues}
                    value={listSource}
                    onChange={setListSource}
                  />
                  <Reason>{SHOPPING_LIST_REPLACES}</Reason>
                  <Field>
                    <FieldLabel htmlFor='list-file'>
                      Or upload a CSV or Excel file
                    </FieldLabel>
                    <Upload
                      id='list-file'
                      accept='.csv,.xlsx,.xls,text/csv'
                      showUploadList={false}
                      beforeUpload={file => {
                        setListFileError('')
                        return readShoppingListFile(file)
                          .then(items => {
                            setListFileName(file.name)
                            setListItems(items)
                            if (!items.length) {
                              setListFileError(
                                'That file has no shopping-list lines. It needs a zone, a quantity, and a product id or product code.',
                              )
                            }
                            return false
                          })
                          .catch(() => {
                            setListItems([])
                            setListFileName('')
                            setListFileError('That file could not be read.')
                            return false
                          })
                      }}
                    >
                      <Button>Choose a file</Button>
                    </Upload>
                    {listFileName ? (
                      <Reason>
                        {listFileName}
                        {listItems.length
                          ? ` · ${listItems.length} ${
                              listItems.length === 1 ? 'line' : 'lines'
                            }`
                          : ''}{' '}
                        <Button
                          type='link'
                          onClick={() => {
                            setListItems([])
                            setListFileName('')
                            setListFileError('')
                          }}
                        >
                          Remove file
                        </Button>
                      </Reason>
                    ) : null}
                  </Field>
                  <Reason>
                    Optional. Use a file instead of a source venue. A
                    shopping-list download works, and so does a sheet with
                    product, zone, default quantity, and par.
                  </Reason>
                  {listFileError && <Reason>{listFileError}</Reason>}
                  {listRun.error && <Reason>{listRun.error}</Reason>}
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={listOpen}
                    groupId={groupId}
                    action='apply_shopping_list'
                    payload={withShareTargets(listRun.payload || {})}
                    onClose={() => setListOpen(false)}
                  />
                </>
              )}
              {copyChoice === 'replace' && (
                <>
                  <SourcePicker
                    venues={group.venues}
                    kind='product'
                    label='Find the product to replace'
                    chosen={fromProduct}
                    onChange={setFromProduct}
                  />
                  <SourcePicker
                    venues={group.venues}
                    kind='product'
                    label='Find the replacement'
                    chosen={toProduct}
                    onChange={setToProduct}
                  />
                  <Reason>{PRODUCT_LOOKUP_NOTE}</Reason>
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={replaceOpen}
                    groupId={groupId}
                    action='replace_product'
                    payload={withShareTargets({
                      fromProductId: fromProduct[0]?._id,
                      toProductId: toProduct[0]?._id,
                      fromProductTitle: fromProduct[0]?.title || '',
                      toProductTitle: toProduct[0]?.title || '',
                    })}
                    onClose={() => setReplaceOpen(false)}
                  />
                </>
              )}
              {copyChoice === 'recipes' && (
                <>
                  <SourcePicker
                    venues={group.venues}
                    kind='recipe'
                    multiple
                    label='Find recipes'
                    chosen={recipes}
                    onChange={setRecipes}
                  />
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={recipesOpen}
                    groupId={groupId}
                    action='share_recipes'
                    payload={withShareTargets({
                      recipeIds: recipes.map(recipe => recipe._id),
                      sourceVenueId: recipes[0]?.venueId,
                    })}
                    onClose={() => setRecipesOpen(false)}
                  />
                </>
              )}
              {copyChoice === 'menu' && (
                <>
                  <SourcePicker
                    venues={group.venues}
                    kind='menu'
                    label='Find a menu'
                    chosen={menu}
                    onChange={setMenu}
                  />
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={menuOpen}
                    groupId={groupId}
                    action='copy_menu'
                    payload={withShareTargets({
                      menuId: menu[0]?._id,
                      sourceVenueId: menu[0]?.venueId,
                    })}
                    onClose={() => setMenuOpen(false)}
                  />
                </>
              )}
              {copyChoice === 'allergens' && (
                <>
                  <Reason>
                    This copies allergens onto the same products. A product this
                    venue does not share is left as it is.
                  </Reason>
                  <VenueSourceSelect
                    id='allergen-source'
                    venues={group.venues}
                    value={allergenSource}
                    onChange={setAllergenSource}
                  />
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={allergensOpen}
                    groupId={groupId}
                    action='copy_allergen_flags'
                    payload={withShareTargets({
                      sourceVenueId: allergenSource,
                    })}
                    sourceTitle={
                      (group.venues || []).find(
                        venue => String(venue._id) === String(allergenSource),
                      )?.title || ''
                    }
                    onClose={() => setAllergensOpen(false)}
                  />
                </>
              )}
              {copyChoice === 'restricted' && (
                <>
                  <VenueSourceSelect
                    id='restriction-source'
                    venues={group.venues}
                    value={restrictionSource}
                    onChange={setRestrictionSource}
                  />
                  {restrictionSuppliers.map(supplier => (
                    <div key={supplier._id} style={{ marginTop: 8 }}>
                      <label style={{ display: 'block' }}>
                        <input
                          type='checkbox'
                          checked={restrictionTicked.includes(
                            String(supplier._id),
                          )}
                          onChange={event => {
                            const id = String(supplier._id)
                            setRestrictionTicked(current =>
                              event.target.checked
                                ? [...current, id]
                                : current.filter(ticked => ticked !== id),
                            )
                          }}
                        />{' '}
                        {supplier.title || 'Supplier'}
                      </label>
                      <label style={{ display: 'block', marginTop: 4 }}>
                        Approver
                        <Input
                          aria-label={`Approver for ${
                            supplier.title || 'supplier'
                          }`}
                          value={supplier.emails || ''}
                          placeholder='Approver email'
                          onChange={event => {
                            const emails = event.target.value
                            setRestrictionSuppliers(current =>
                              current.map(row =>
                                String(row._id) === String(supplier._id)
                                  ? { ...row, emails }
                                  : row,
                              ),
                            )
                          }}
                          style={{ marginTop: 4, maxWidth: 360 }}
                        />
                      </label>
                    </div>
                  ))}
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={restrictionsOpen}
                    groupId={groupId}
                    action='copy_restricted_suppliers'
                    payload={withShareTargets({
                      sourceVenueId: restrictionSource,
                      supplierIds: restrictionTicked,
                      approvers: restrictionSuppliers
                        .filter(supplier =>
                          restrictionTicked.includes(String(supplier._id)),
                        )
                        .map(supplier => ({
                          supplierId: supplier._id,
                          title: supplier.title || 'Supplier',
                          emails: approvalEmailsFromText(supplier.emails),
                        })),
                    })}
                    onClose={() => setRestrictionsOpen(false)}
                  />
                </>
              )}
              {copyChoice === 'revenue' && (
                <>
                  <VenueSourceSelect
                    id='revenue-source'
                    venues={group.venues}
                    value={revenueSource}
                    onChange={setRevenueSource}
                  />
                  <Reason>{REVENUE_SHARE_DOES_NOT_COPY_SALES}</Reason>
                  {revenueCategories.map(category => (
                    <label
                      key={category._id}
                      style={{ display: 'block', marginTop: 8 }}
                    >
                      <input
                        type='checkbox'
                        checked={revenueTicked.includes(String(category._id))}
                        onChange={event => {
                          const id = String(category._id)
                          setRevenueTicked(current =>
                            event.target.checked
                              ? [...current, id]
                              : current.filter(ticked => ticked !== id),
                          )
                        }}
                      />{' '}
                      {revenueCategoryTickLabel(category)}
                    </label>
                  ))}
                  {revenueLoading && <Reason>Loading</Reason>}
                  {revenueSource &&
                    !revenueLoading &&
                    revenueCategories.length === 0 && (
                      <Reason>This venue has no revenue categories.</Reason>
                    )}
                  {revenueNote && <Reason>{revenueNote}</Reason>}
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={revenueOpen}
                    groupId={groupId}
                    action='assign_revenue_categories'
                    payload={withShareTargets({
                      sourceVenueId: revenueSource,
                      categoryIds: revenueTicked,
                    })}
                    onClose={() => setRevenueOpen(false)}
                  />
                </>
              )}
              {copyChoice === 'invites' && (
                <>
                  <label style={{ display: 'block', marginTop: 8 }}>
                    <input
                      type='radio'
                      name='invite-choice'
                      checked={inviteChoice === 'people'}
                      onChange={() => setInviteChoice('people')}
                    />{' '}
                    {INVITE_SHARE_PEOPLE}
                  </label>
                  <label style={{ display: 'block', marginTop: 8 }}>
                    <input
                      type='radio'
                      name='invite-choice'
                      checked={inviteChoice === 'email'}
                      onChange={() => setInviteChoice('email')}
                    />{' '}
                    {INVITE_BY_EMAIL}
                  </label>
                  {inviteChoice === 'email' && (
                    <>
                      <Field>
                        <FieldLabel htmlFor='invite-email'>
                          Email address
                        </FieldLabel>
                        <Input
                          id='invite-email'
                          type='email'
                          value={inviteEmail}
                          placeholder='Email address'
                          onChange={event => setInviteEmail(event.target.value)}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor='invite-name'>Full name</FieldLabel>
                        <Input
                          id='invite-name'
                          value={inviteName}
                          placeholder='e.g. Jane Smith'
                          onChange={event => setInviteName(event.target.value)}
                        />
                      </Field>
                      <Field>
                        <FieldLabel>Role</FieldLabel>
                        <VenueRoleSelect
                          value={inviteRole}
                          onChange={setInviteRole}
                          style={{ width: '100%', maxWidth: 360 }}
                        />
                      </Field>
                      <Reason>Tick the venues, or all of them.</Reason>
                      <Row>
                        <QuietButton
                          disabled={(group.venues || []).length === 0}
                          onClick={() => {
                            const ids = (group.venues || []).map(venue =>
                              String(venue._id),
                            )
                            const everyVenue =
                              ids.length > 0 &&
                              ids.every(id => inviteVenueIds.includes(id))
                            setInviteVenueIds(everyVenue ? [] : ids)
                          }}
                        >
                          {`Select all (${(group.venues || []).length})`}
                        </QuietButton>
                        <QuietButton
                          disabled={inviteVenueIds.length === 0}
                          onClick={() => setInviteVenueIds([])}
                        >
                          Deselect all
                        </QuietButton>
                      </Row>
                      {(group.venues || []).map(venue => (
                        <label
                          key={venue._id}
                          style={{ display: 'block', marginTop: 8 }}
                        >
                          <input
                            type='checkbox'
                            checked={inviteVenueIds.includes(String(venue._id))}
                            onChange={event => {
                              const id = String(venue._id)
                              setInviteVenueIds(current =>
                                event.target.checked
                                  ? [...current, id]
                                  : current.filter(ticked => ticked !== id),
                              )
                            }}
                          />{' '}
                          {venue.title}
                        </label>
                      ))}
                    </>
                  )}
                  {inviteChoice === 'people' && (
                    <>
                      <VenueSourceSelect
                        id='people-source'
                        venues={group.venues}
                        value={peopleSource}
                        onChange={value => {
                          setPeopleSource(value)
                          setPeopleTicked([])
                          setPeopleVenueIds([])
                        }}
                      />
                      {venuePeople.map(person => (
                        <label
                          key={person._id}
                          style={{ display: 'block', marginTop: 8 }}
                        >
                          <input
                            type='checkbox'
                            checked={peopleTicked.includes(String(person._id))}
                            onChange={event => {
                              const id = String(person._id)
                              setPeopleTicked(current =>
                                event.target.checked
                                  ? [...current, id]
                                  : current.filter(ticked => ticked !== id),
                              )
                            }}
                          />{' '}
                          {person.name || person.email}
                          {' · '}
                          {person.email}
                          {' · '}
                          {getVenueRoleDisplayName(person.role)}
                        </label>
                      ))}
                      {peopleSource && venuePeople.length === 0 && (
                        <Reason>This venue has no people to share.</Reason>
                      )}
                      {peopleSource && (
                        <>
                          <Reason>Choose the venues to share them with.</Reason>
                          <Row>
                            <QuietButton
                              disabled={shareDestinations.length === 0}
                              onClick={() => {
                                const ids = shareDestinations.map(venue =>
                                  String(venue._id),
                                )
                                const everyVenue =
                                  ids.length > 0 &&
                                  ids.every(id => peopleVenueIds.includes(id))
                                setPeopleVenueIds(everyVenue ? [] : ids)
                              }}
                            >
                              {`Select all (${shareDestinations.length})`}
                            </QuietButton>
                            <QuietButton
                              disabled={peopleVenueIds.length === 0}
                              onClick={() => setPeopleVenueIds([])}
                            >
                              Deselect all
                            </QuietButton>
                          </Row>
                          {shareDestinations.map(venue => (
                            <label
                              key={venue._id}
                              style={{ display: 'block', marginTop: 8 }}
                            >
                              <input
                                type='checkbox'
                                checked={peopleVenueIds.includes(
                                  String(venue._id),
                                )}
                                onChange={event => {
                                  const id = String(venue._id)
                                  setPeopleVenueIds(current =>
                                    event.target.checked
                                      ? [...current, id]
                                      : current.filter(ticked => ticked !== id),
                                  )
                                }}
                              />{' '}
                              {venue.title}
                            </label>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={invitesOpen}
                    groupId={groupId}
                    action='invite_people'
                    payload={
                      inviteChoice === 'email'
                        ? {
                            people: [
                              {
                                email: inviteEmail.trim(),
                                name: inviteName.trim(),
                                role: inviteRole,
                              },
                            ],
                            venueIds: inviteVenueIds,
                            inviteEmail: {
                              email: inviteEmail.trim(),
                              roleLabel: getVenueRoleDisplayName(inviteRole),
                              knownAccount: inviteKnown,
                              venueTitles: (group.venues || [])
                                .filter(venue =>
                                  inviteVenueIds.includes(String(venue._id)),
                                )
                                .map(venue => venue.title),
                            },
                          }
                        : {
                            people: venuePeople
                              .filter(person =>
                                peopleTicked.includes(String(person._id)),
                              )
                              .map(person => ({
                                email: person.email,
                                name: person.name || person.email,
                                role: person.role,
                                roleLabel: getVenueRoleDisplayName(person.role),
                              })),
                            venueIds: peopleVenueIds,
                            shareVenueTitles: shareDestinations
                              .filter(venue =>
                                peopleVenueIds.includes(String(venue._id)),
                              )
                              .map(venue => venue.title),
                          }
                    }
                    onClose={() => setInvitesOpen(false)}
                  />
                </>
              )}
              {(copyChoice === 'person' || copyChoice === 'remove') && (
                <>
                  <Reason>
                    {copyChoice === 'remove'
                      ? 'This takes someone off the venues you tick. A venue they are not on is left. One email covers the whole removal.'
                      : 'This changes someone who is already on the venue. Inviting them is separate.'}
                  </Reason>
                  {copyChoice === 'remove' && (
                    <>
                      <Reason>
                        Search by name or email for someone still on a venue in
                        this group.
                      </Reason>
                      <Input
                        value={groupPeopleQuery}
                        onChange={event =>
                          setGroupPeopleQuery(event.target.value)
                        }
                        placeholder='Search name or email'
                      />
                      {groupPeopleReady && groupPeople.length === 0 && (
                        <Reason>{GROUP_PEOPLE_EMPTY}</Reason>
                      )}
                      {groupPeople.map(person => (
                        <QuietButton
                          key={person.email}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            marginTop: 8,
                          }}
                          onClick={() => {
                            setPersonRoles(
                              personRolesFromSearchPick({
                                venues: group.venues,
                                places: person.venues,
                              }),
                            )
                            setPersonEmail(person.email)
                            setPersonVenueIds(
                              (person.venues || []).map(venue =>
                                String(venue.venueId),
                              ),
                            )
                            setGroupPeopleQuery('')
                            setGroupPeople([])
                            setGroupPeopleReady(false)
                          }}
                        >
                          <span style={{ display: 'block' }}>
                            {`${person.name || person.email} · ${person.email}`}
                          </span>
                          <span
                            style={{
                              display: 'block',
                              marginTop: 4,
                              opacity: 0.72,
                              fontWeight: 400,
                            }}
                          >
                            {(person.venues || [])
                              .map(
                                venue =>
                                  `${venue.title} · ${personVenueRoleLabel({
                                    owns: venue.owns,
                                    onVenue: true,
                                    role: venue.role,
                                  })}`,
                              )
                              .join(', ')}
                          </span>
                        </QuietButton>
                      ))}
                    </>
                  )}
                  <Input
                    value={personEmail}
                    onChange={event => {
                      setPersonEmail(event.target.value)
                      setPersonRoles({})
                    }}
                    placeholder='Email'
                  />
                  {copyChoice === 'person' && (
                    <FieldSelect
                      id='person-role'
                      label='Role'
                      value={personRole}
                      onChange={setPersonRole}
                      placeholder='Choose a role'
                    >
                      <Select.Option value='VENUE_ADMIN'>Admin</Select.Option>
                      <Select.Option value='HEAD_CHEF_GM'>
                        Head Chef / GM
                      </Select.Option>
                      <Select.Option value='CHEF'>Chef</Select.Option>
                      <Select.Option value='ORDER_ONLY'>
                        Order Only
                      </Select.Option>
                      <Select.Option value='KITCHEN_PORTER'>
                        Kitchen Porter
                      </Select.Option>
                    </FieldSelect>
                  )}
                  <>
                    <Reason>
                      {copyChoice === 'remove'
                        ? 'Tick the venues to take them off, or all of them they are on.'
                        : 'Tick the venues this role applies to, or all of them.'}
                    </Reason>
                    <Row>
                      <QuietButton
                        disabled={personSelectAllIds.length === 0}
                        onClick={() => {
                          const everyVenue =
                            personSelectAllIds.length > 0 &&
                            personSelectAllIds.every(id =>
                              personVenueIds.includes(id),
                            )
                          setPersonVenueIds(
                            everyVenue ? [] : personSelectAllIds,
                          )
                        }}
                      >
                        {`Select all where they’re on (${personSelectAllIds.length})`}
                      </QuietButton>
                      <QuietButton
                        disabled={personVenueIds.length === 0}
                        onClick={() => setPersonVenueIds([])}
                      >
                        Deselect all
                      </QuietButton>
                    </Row>
                    {(group.venues || []).map(venue => (
                      <label
                        key={venue._id}
                        style={{ display: 'block', marginTop: 8 }}
                      >
                        <input
                          type='checkbox'
                          checked={personVenueIds.includes(String(venue._id))}
                          onChange={event => {
                            const id = String(venue._id)
                            setPersonVenueIds(current =>
                              event.target.checked
                                ? [...current, id]
                                : current.filter(ticked => ticked !== id),
                            )
                          }}
                        />{' '}
                        {venue.title}
                        {personRoles[String(venue._id)]
                          ? ` · ${personVenueRoleLabel(
                              personRoles[String(venue._id)],
                            )}`
                          : ''}
                      </label>
                    ))}
                  </>
                  <VenueGroupRunDialog
                    onFinished={rememberShare}
                    open={personOpen}
                    groupId={groupId}
                    action='change_venue_person'
                    payload={personRunPayload({
                      email: personEmail.trim(),
                      role: personRole,
                      venueIds: personVenueIds,
                      venues: group.venues,
                      removing: copyChoice === 'remove',
                    })}
                    onClose={() => setPersonOpen(false)}
                  />
                </>
              )}
              {snapshot && (
                <Snapshot>
                  <SnapshotSummary>{snapshot.summary}</SnapshotSummary>
                  {snapshot.shown.map((name, index) => (
                    <SnapshotLine key={`${name}-${index}`}>{name}</SnapshotLine>
                  ))}
                  {snapshot.more > 0 && (
                    <SnapshotLine>and {snapshot.more} more</SnapshotLine>
                  )}
                </Snapshot>
              )}
            </CopyStep>
            <CopyStep
              step={showShareTargetPicker ? '4' : '3'}
              title={
                copyChoice === 'remove'
                  ? 'Review and remove'
                  : 'Review and send'
              }
              line={
                copyChoice === 'remove'
                  ? 'Review opens a confirmation. Nothing is removed until you confirm.'
                  : 'Review opens a confirmation. Nothing is copied until you confirm.'
              }
            >
              {copyChoice === 'shopping' && (
                <Button
                  type='primary'
                  disabled={!listRun.payload || !shareTargetsReady}
                  onClick={() => setListOpen(true)}
                >
                  Review shopping list
                </Button>
              )}
              {copyChoice === 'replace' && (
                <Button
                  type='primary'
                  disabled={
                    !shareTargetsReady ||
                    !fromProduct[0] ||
                    !toProduct[0] ||
                    String(fromProduct[0]._id) === String(toProduct[0]._id)
                  }
                  onClick={() => setReplaceOpen(true)}
                >
                  Review product replace
                </Button>
              )}
              {copyChoice === 'recipes' && (
                <Button
                  type='primary'
                  disabled={recipes.length === 0 || !shareTargetsReady}
                  onClick={() => setRecipesOpen(true)}
                >
                  Review recipes
                </Button>
              )}
              {copyChoice === 'menu' && (
                <Button
                  type='primary'
                  disabled={!menu[0] || !shareTargetsReady}
                  onClick={() => setMenuOpen(true)}
                >
                  Review menu
                </Button>
              )}
              {copyChoice === 'allergens' && (
                <Button
                  type='primary'
                  disabled={!allergenSource || !shareTargetsReady}
                  onClick={() => setAllergensOpen(true)}
                >
                  Review allergens
                </Button>
              )}
              {copyChoice === 'restricted' && (
                <Button
                  type='primary'
                  disabled={
                    !canReviewRestrictedSuppliers(restrictionTicked) ||
                    !shareTargetsReady
                  }
                  onClick={() => setRestrictionsOpen(true)}
                >
                  Review restricted suppliers
                </Button>
              )}
              {copyChoice === 'invites' && (
                <>
                  <Button
                    type='primary'
                    disabled={
                      !canReviewInvites({
                        inviteChoice,
                        peopleTicked,
                        peopleVenueIds,
                        inviteEmail,
                        inviteVenueIds,
                      })
                    }
                    onClick={() => {
                      if (
                        !canReviewInvites({
                          inviteChoice,
                          peopleTicked,
                          peopleVenueIds,
                          inviteEmail,
                          inviteVenueIds,
                        })
                      ) {
                        return undefined
                      }
                      if (inviteChoice !== 'email') {
                        setInvitesOpen(true)
                        return undefined
                      }
                      if (inviteCheckBusy.current) return undefined
                      inviteCheckBusy.current = true
                      return API.post('api2', 'admin/checkEmailExists', {
                        body: { email: inviteEmail.trim() },
                      })
                        .then(response => {
                          setInviteKnown(Boolean(response?.success))
                          setInvitesOpen(true)
                          return null
                        })
                        .catch(error => {
                          if (error?.response?.status === 404) {
                            if (!inviteName.trim()) {
                              RejectedNotification({
                                message: 'Enter their name.',
                              })
                              return null
                            }
                            setInviteKnown(false)
                            setInvitesOpen(true)
                            return null
                          }
                          RejectedNotification({
                            message:
                              error?.response?.data?.message ||
                              'Could not check that email.',
                          })
                          return null
                        })
                        .finally(() => {
                          inviteCheckBusy.current = false
                        })
                    }}
                  >
                    Review invites
                  </Button>
                  {!canReviewInvites({
                    inviteChoice,
                    peopleTicked,
                    peopleVenueIds,
                    inviteEmail,
                    inviteVenueIds,
                  }) ? (
                    <Reason>{INVITE_REVIEW_HINT}</Reason>
                  ) : null}
                </>
              )}
              {(copyChoice === 'person' || copyChoice === 'remove') && (
                <Button
                  type='primary'
                  disabled={!personEmail.trim() || personVenueIds.length === 0}
                  onClick={() => setPersonOpen(true)}
                >
                  {copyChoice === 'remove'
                    ? 'Review this removal'
                    : 'Review this person'}
                </Button>
              )}
              {copyChoice === 'revenue' && (
                <Button
                  type='primary'
                  disabled={revenueTicked.length === 0 || !shareTargetsReady}
                  onClick={() => setRevenueOpen(true)}
                >
                  Review revenue categories
                </Button>
              )}
            </CopyStep>
            {shareNote && (
              <ShareNote>{formatShareConfirmation(shareNote)}</ShareNote>
            )}
          </GroupSurface>
        )}
        {pageTab === GROUP_PAGE_TABS.venues && (
          <GroupSurface title='Add venues'>
            <Reason>
              You can add any venue you can manage, including one under a
              different owner. A venue you cannot change is left out, and a
              share only lands on venues you can manage.
            </Reason>
            <FilterCaption>Venues you can add</FilterCaption>
            <FilterRow>
              <FilterField label='Name' $grow>
                <Input
                  value={addQuery}
                  onChange={event => setAddQuery(event.target.value)}
                  placeholder='Name contains'
                />
              </FilterField>
              <FilterField label='Plan'>
                <Select
                  value={planFilter}
                  onChange={setPlanFilter}
                  style={{ width: 140 }}
                >
                  <Select.Option value='all'>All plans</Select.Option>
                  <Select.Option value='Free Plan'>Free</Select.Option>
                  <Select.Option value='Starter Plan'>Starter</Select.Option>
                  <Select.Option value='Growth Plan'>Growth</Select.Option>
                  <Select.Option value='Pro Plan'>Pro</Select.Option>
                </Select>
              </FilterField>
              {isOmsAdmin && (
                <FilterField label='Brand'>
                  <Select
                    value={brandFilter}
                    onChange={setBrandFilter}
                    style={{ width: 140 }}
                  >
                    <Select.Option value='all'>All brands</Select.Option>
                    <Select.Option value='TFR'>TFR</Select.Option>
                    <Select.Option value='GREEN'>GREEN</Select.Option>
                    <Select.Option value='JUST'>JUST</Select.Option>
                  </Select>
                </FilterField>
              )}
              <FilterField label='Activity'>
                <ActivitySelect
                  value={activityFilter}
                  onChange={setActivityFilter}
                />
              </FilterField>
            </FilterRow>
            {!addQuery.trim() && !fillActive && addResults.length === 0 && (
              <Reason>Search for a name, or paste a list.</Reason>
            )}
            {addTruncated && addQuery.trim() && !fillActive && (
              <Reason>More venues match. Type more of the name.</Reason>
            )}
            {fillLoading && <Reason>Loading venues…</Reason>}
            {pasteFeedback ? <Reason>{pasteFeedback}</Reason> : null}
            {(fillActive || addResults.length > 0) && !fillLoading && (
              <Reason>
                {addableVenues.length} venues match
                {addableVenues.length > addPageSize
                  ? `. Showing ${addRangeStart}–${addRangeEnd}.`
                  : '.'}
              </Reason>
            )}
            {(fillActive || addResults.length > 0) &&
              !fillLoading &&
              addPageCount > 1 && (
                <Row>
                  <Button
                    disabled={safeAddPage <= 1}
                    onClick={() => setAddPage(page => Math.max(1, page - 1))}
                  >
                    Previous page
                  </Button>
                  <Button
                    disabled={safeAddPage >= addPageCount}
                    onClick={() => setAddPage(page => page + 1)}
                  >
                    Next page
                  </Button>
                </Row>
              )}
            {(addQuery.trim() || fillActive || addResults.length > 0) && (
              <VenueList>
                {shownAddVenues.map(venue => (
                  <VenueRow key={venue._id}>
                    <label>
                      <input
                        type='checkbox'
                        checked={toAdd.some(
                          item => String(item._id) === String(venue._id),
                        )}
                        disabled={venue.canChange === false}
                        onChange={() =>
                          setToAdd(current =>
                            current.some(
                              item => String(item._id) === String(venue._id),
                            )
                              ? current.filter(
                                  item =>
                                    String(item._id) !== String(venue._id),
                                )
                              : [...current, venue],
                          )
                        }
                      />
                      {venue.title}
                    </label>
                    {venue.canChange === false && (
                      <Reason>{venue.reason}</Reason>
                    )}
                  </VenueRow>
                ))}
              </VenueList>
            )}
            <Field>
              <FieldLabel htmlFor='add-paste'>Paste a list of names</FieldLabel>
              <Input.TextArea
                id='add-paste'
                rows={3}
                value={pasted}
                onChange={event => setPasted(event.target.value)}
                placeholder='One venue name per line'
              />
            </Field>
            <Row>
              <QuietButton disabled={!pasted.trim()} onClick={findPasted}>
                Select matching venues
              </QuietButton>
              {fillActive && (
                <QuietButton
                  disabled={addableVenues.length === 0 || fillLoading}
                  onClick={confirmAddAll}
                >
                  {`Add all venues in this filter (${addableVenues.length})`}
                </QuietButton>
              )}
              <Button
                type='primary'
                disabled={chosenToAdd.length === 0}
                onClick={confirmAdd}
              >
                Add selected
              </Button>
            </Row>
            {pasteAmbiguous.map(line => (
              <Reason key={line}>{line}</Reason>
            ))}
          </GroupSurface>
        )}
        <ArchiveFoot>
          <QuietButton onClick={openDuplicate}>Save as new group</QuietButton>
          {canEditMeta ? (
            <ArchiveLink type='button' onClick={archive}>
              Archive group
            </ArchiveLink>
          ) : null}
        </ArchiveFoot>
        <Modal
          title='Duplicate this group?'
          visible={duplicateOpen}
          okText='Duplicate'
          confirmLoading={duplicateBusy}
          okButtonProps={{ disabled: !duplicateName.trim() }}
          onOk={confirmDuplicate}
          onCancel={() => {
            if (!duplicateBusy) setDuplicateOpen(false)
          }}
          destroyOnClose
        >
          <p>
            Copies the venues you can manage into a new group. Shares and last
            run stay on the original. Use this for a lasting bars or kitchens
            layout; for one share, untick venues on the Share tab instead.
          </p>
          <Field>
            <FieldLabel htmlFor='duplicate-group-name'>
              New group name
            </FieldLabel>
            <Input
              id='duplicate-group-name'
              value={duplicateName}
              maxLength={80}
              onChange={event => setDuplicateName(event.target.value)}
              onPressEnter={confirmDuplicate}
            />
          </Field>
        </Modal>
      </GroupPage>
    </FadeIn>
  )
}

const GroupSubnav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 6;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 0 4px;
  padding: 8px 0;
  background: #f7fbec;
  border-bottom: 1px solid #e4efc8;
`

const GroupSubnavTab = styled.button`
  appearance: none;
  border: 1px solid transparent;
  border-radius: 8px;
  background: ${props => (props.$active ? '#fff' : 'transparent')};
  border-color: ${props => (props.$active ? '#d7e6a8' : 'transparent')};
  color: ${props => (props.$active ? '#132d07' : '#5c6560')};
  font-size: 14px;
  font-weight: ${props => (props.$active ? 650 : 500)};
  line-height: 1.3;
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    color: #132d07;
    background: #fff;
  }
`

const ActivityLine = styled.span`
  display: block;
  color: ${props => props.$ink || '#5c6560'};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
`

const RunLine = styled.p`
  margin: 8px 0 0;
  color: ${props => props.$ink || '#5c6560'};
  font-size: 13px;
  line-height: 1.4;
`

const ActivityDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
  background: ${props => props.$color || '#5c6560'};
  vertical-align: 1px;
`

const CountButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.$ink || 'inherit'};
  font: inherit;
  font-weight: ${props => (props.$pressed ? 700 : 600)};
  cursor: pointer;
  text-decoration: ${props => (props.$pressed ? 'underline' : 'none')};

  &:hover {
    text-decoration: underline;
  }
`

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: 10px;
  color: #5c6560;
  font-size: 13px;
  font-weight: 600;

  &:hover,
  &:focus {
    color: #132d07;
  }
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const NameInput = styled(Input)`
  flex: 1 1 auto;
  height: 44px;
  font-size: 1.35rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  color: #132d07;
`

const CopyStepBox = styled.section`
  margin-top: 18px;

  &:first-child {
    margin-top: 0;
  }
`

const CopyStepTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 650;
  letter-spacing: -0.02em;
  color: #132d07;
`

const CopyStepLine = styled.p`
  margin: 4px 0 10px;
  color: #5c6560;
  font-size: 13px;
  line-height: 1.45;
`

const CopyStep = ({ step, title, line, children }) => (
  <CopyStepBox>
    <CopyStepTitle>
      {step}. {title}
    </CopyStepTitle>
    <CopyStepLine>{line}</CopyStepLine>
    {children}
  </CopyStepBox>
)

const ShareNote = styled.p`
  margin: 16px 0 0;
  color: #132d07;
  font-weight: 650;
  line-height: 1.45;
`

const Snapshot = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #eef3e4;
  border-radius: 8px;
  background: #fff;
`

const SnapshotSummary = styled.p`
  margin: 0 0 6px;
  font-weight: 650;
  color: #132d07;
`

const SnapshotLine = styled.p`
  margin: 0;
  color: #5c6560;
  line-height: 1.45;
`

const Row = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const FilterCaption = styled.p`
  margin: 0 0 8px;
  font-weight: 650;
  color: #132d07;
`

const FilterRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: flex-end;
`

const FilterFieldBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: ${({ $grow }) => ($grow ? '1 1 12rem' : '0 0 auto')};
  min-width: ${({ $grow }) => ($grow ? '8rem' : 'auto')};

  .ant-input {
    width: 100%;
  }
`

const FilterFieldLabel = styled.span`
  color: #5c6560;
  font-size: 12px;
  line-height: 1.2;
`

const FilterField = ({ label, $grow, children }) => (
  <FilterFieldBox $grow={$grow}>
    <FilterFieldLabel>{label}</FilterFieldLabel>
    {children}
  </FilterFieldBox>
)

const VenueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0;

  label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
`

const VenueRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid #eef3e4;
  border-radius: 8px;
  background: #fff;
`

const Glance = styled.span`
  color: #595959;
  font-size: 13px;
`

const MismatchRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
`

const MismatchButton = styled.button`
  display: grid;
  grid-template-columns: minmax(10rem, 1fr) minmax(12rem, 2fr) auto;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #eef3e4;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  font: inherit;
`

const MemberRow = styled(VenueRow)`
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) 13rem auto;
  justify-content: stretch;
  align-items: center;
`

const SalesHead = styled.div`
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) 13rem auto;
  align-items: end;
  padding: 0 10px 4px;
`

const SalesHeadText = styled.div`
  color: #5c6560;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  text-align: right;
`

const SalesCell = styled.span`
  color: #132d07;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  text-align: right;
  font-variant-numeric: tabular-nums;
`

const SalesLink = styled(Link)`
  display: block;
  color: #132d07;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover,
  &:focus {
    color: #237804;
    text-decoration: underline;
  }
`

const SortSales = styled.button`
  display: block;
  width: 100%;
  margin-top: 2px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => (props.$pressed ? '#132d07' : '#5c6560')};
  font-size: 12px;
  font-weight: ${props => (props.$pressed ? '700' : '500')};
  text-align: right;
  text-decoration: ${props => (props.$pressed ? 'underline' : 'none')};
  cursor: pointer;

  &:hover {
    color: #132d07;
    text-decoration: underline;
  }
`

const QuietMark = styled.span`
  color: #5c6560;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`

const VenueLinks = styled.span`
  display: flex;
  align-items: center;
  gap: 16px;

  a {
    color: #5c6560;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  }

  a:hover,
  a:focus {
    color: #132d07;
  }
`

const Reason = styled.p`
  display: block;
  width: 100%;
  margin: 0 0 12px;
  color: #6b6256;
  line-height: 1.45;
`

const QuietButton = styled(Button)`
  && {
    color: #5c6560;
    background: #fff;
    border-color: #d5ddd0;
    box-shadow: none;
  }

  &&:hover,
  &&:focus {
    color: #132d07;
    background: #fff;
    border-color: #b7c4ae;
  }
`

const ArchiveFoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  justify-content: flex-start;
  margin-top: 8px;
`

const ArchiveLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: #8f4a42;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus {
    color: #6e2e28;
  }
`

const fieldStyle = { width: '100%', maxWidth: 360 }

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`

const FieldLabel = styled.label`
  color: #3d4a38;
`

const copyChoiceOption = (value, label, tip) => (
  <Select.Option key={value} value={value}>
    <Tooltip
      title={tip}
      mouseEnterDelay={0}
      mouseLeaveDelay={0}
      placement='right'
    >
      <span>{label}</span>
    </Tooltip>
  </Select.Option>
)

const SHARE_SELECT_DROPDOWN_STYLE = { zIndex: 1100 }

const FieldSelect = ({
  id,
  label,
  ariaLabel,
  value,
  onChange,
  onDropdownVisibleChange,
  placeholder,
  children,
}) => (
  <Field>
    {label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
    <Select
      id={id}
      aria-label={ariaLabel || label || placeholder || 'Choose'}
      value={value || undefined}
      placeholder={placeholder}
      onChange={onChange}
      onDropdownVisibleChange={onDropdownVisibleChange}
      style={fieldStyle}
      dropdownStyle={SHARE_SELECT_DROPDOWN_STYLE}
      getPopupContainer={() => document.body}
      dropdownMatchSelectWidth={false}
    >
      {children}
    </Select>
  </Field>
)

const VenueSourceSelect = ({ id, venues, value, onChange }) => (
  <FieldSelect
    id={id}
    label='Source venue'
    value={value}
    onChange={onChange}
    placeholder='Choose a venue'
  >
    {(venues || []).map(venue => (
      <Select.Option key={venue._id} value={venue._id}>
        {venue.title}
      </Select.Option>
    ))}
  </FieldSelect>
)

const ActivitySelect = ({ value, onChange }) => (
  <Select
    value={value}
    onChange={onChange}
    style={{ width: 180 }}
    dropdownMatchSelectWidth={false}
  >
    <Select.Option value='all'>All venues</Select.Option>
    <Select.Option value='green'>
      Active — last order within 30 days
    </Select.Option>
    <Select.Option value='amber'>
      Dormant — last order 31 to 365 days ago
    </Select.Option>
    <Select.Option value='red'>
      Inactive — last order over a year ago
    </Select.Option>
    <Select.Option value='purple'>Never ordered</Select.Option>
  </Select>
)

export default VenueGroupPage
